/* global __firebase_config, __app_id, __initial_auth_token */
import React, { useState, useEffect, useRef } from 'react';
import { 
  UploadCloud, Settings, Printer, CheckCircle, ChevronRight, AlertCircle,
  Loader2, RefreshCw, BookOpen, Home, Share2, Users, Award, User, Link as LinkIcon, 
  Trash2, UserPlus, Clock, Edit2, Check, X, Copy, LogOut, Mail, Lock,
  BarChart2, Target, AlertTriangle, ChevronDown, ChevronUp, Edit3, CheckSquare, Search
} from 'lucide-react';

// --- Firebase Imports ---
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken,
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { getFirestore, collection, addDoc, onSnapshot, doc, deleteDoc, updateDoc, getDoc, setDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// --- Custom Brand Logo ---
const RotorShedLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="2.5" fill="currentColor" />
    <path d="M9.5 2v4c0 3.5 5 3.5 5 0V2" />
    <path d="M12 8v4" />
    <path d="M12 12l7.8 4.5" />
    <path d="M12 12l-7.8 4.5" />
  </svg>
);

// --- Dynamic Brand Logo ---
const BrandLogo = ({ className, customLogo }) => {
  if (customLogo) {
    return <img src={customLogo} alt="Brand Logo" className={`${className} object-contain`} />;
  }
  return <RotorShedLogo className={className} />;
};

// Initialize Firebase
const firebaseConfig = typeof __firebase_config !== 'undefined' 
  ? JSON.parse(__firebase_config) 
  : {
      apiKey: "AIzaSyD4o8A0dhCoK3l0YFjXbFYNGp3wjfuHT34",
      authDomain: "docuquiz-3f011.firebaseapp.com",
      projectId: "docuquiz-3f011",
      storageBucket: "docuquiz-3f011.firebasestorage.app",
      messagingSenderId: "647906403777",
      appId: "1:647906403777:web:fc26993ff3e3ebdedd3b40",
      measurementId: "G-NQP1VK5PBS"
    };

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const cloudFunctions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();
const appId = typeof __app_id !== 'undefined' ? __app_id : "docuquiz-3f011"; 

// --- Canvas AI Helper with UAE Bypass ---
const fetchCanvasGemini = async (prompt, schema = null, customKey = "") => {
  const apiKey = customKey || ""; 
  const model = customKey ? "gemini-2.5-pro" : "gemini-2.5-flash-preview-09-2025";
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`;
  
  const generationConfig = {};
  if (schema) {
    generationConfig.responseMimeType = "application/json";
    generationConfig.responseSchema = schema;
  } else {
    generationConfig.responseMimeType = "text/plain";
  }

  const payload = {
    contents: [{ parts: [{ text: prompt }] }],
    generationConfig
  };

  let retries = 5;
  let delay = 1000;
  
  while (retries > 0) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
         if (response.status === 429) throw new Error("Quota Exceeded (429). Please check your Google Cloud Billing.");
         if (response.status === 401) throw new Error("Unauthorized (401). Check if your API key is correct.");
         throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const text = result.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!text) throw new Error("Empty response from AI");
      
      return schema ? JSON.parse(text) : text;
    } catch (error) {
      retries--;
      if (retries === 0) throw error;
      await new Promise(r => setTimeout(r, delay));
      delay *= 2;
    }
  }
};

// --- Foolproof Markdown Parser ---
const parseMarkdownToHTML = (text) => {
  if (!text) return "";
  
  let html = String(text).split('\\n').join('\n').split('\\r').join('');
  html = html.split('\\*').join('*').split('\\#').join('#').split('\\-').join('-');
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/^###\s+(.*)$/gm, '<h3>$1</h3>');
  html = html.replace(/^##\s+(.*)$/gm, '<h2>$1</h2>');
  html = html.replace(/^#\s+(.*)$/gm, '<h1>$1</h1>');
  html = html.replace(/^[\s]*[-*]\s+(.*)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*?<\/li>\n?)+/gm, '<ul>$&</ul>');
  html = html.replace(/\n/g, '<br/>');
  
  const blockTags = ['h1', 'h2', 'h3', 'ul', 'li'];
  blockTags.forEach(tag => {
    const regexStart = new RegExp(`(<br\\/>)+\\s*<${tag}>`, 'g');
    const regexEnd = new RegExp(`</${tag}>\\s*(<br\\/>)+`, 'g');
    html = html.replace(regexStart, `<${tag}>`);
    html = html.replace(regexEnd, `</${tag}>`);
  });
  
  html = html.replace(/^(<br\/>)+/, '');
  html = html.replace(/(<br\/>)+$/, '');
  
  return html;
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export default function App() {
  // --- Auth & Data State ---
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [rawAllQuizzes, setRawAllQuizzes] = useState([]);
  const [allQuizKeys, setAllQuizKeys] = useState({});
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [authErrorMsg, setAuthErrorMsg] = useState("");

  // --- App State ---
  const [view, setView] = useState("home"); 
  const [activeQuizId, setActiveQuizId] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [manualAiMode, setManualAiMode] = useState(false);
  const [canvasApiKey, setCanvasApiKey] = useState("");
  
  // --- Read-Only Audit Mode States ---
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [viewingAdminId, setViewingAdminId] = useState(null);
  const [auditKeyInput, setAuditKeyInput] = useState("");
  const [adminAuditKey, setAdminAuditKey] = useState(null);
  const [adminKeyHistory, setAdminKeyHistory] = useState([]);
  const [keyGraphRange, setKeyGraphRange] = useState('7');
  
  // --- Instructor Login State ---
  const [loginEmail, setLoginEmail] = useState("");
  const [loginError, setLoginError] = useState("");
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // --- Instructor Creation State ---
  const [file, setFile] = useState(null);
  const [pdfText, setPdfText] = useState("");
  const [status, setStatus] = useState("idle"); 
  const [statusMessage, setStatusMessage] = useState("");
  const [difficulty, setDifficulty] = useState(3);
  const [numQuestions, setNumQuestions] = useState(10);
  const [numOptions, setNumOptions] = useState(4);
  const [topicFocus, setTopicFocus] = useState(""); 
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [courseName, setCourseName] = useState(""); 
  const [selectedStudents, setSelectedStudents] = useState([]); 
  const [importText, setImportText] = useState(""); 
  const [startsAt, setStartsAt] = useState("");
  const [expiresAt, setExpiresAt] = useState(""); 
  const [timeLimit, setTimeLimit] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [draftQuiz, setDraftQuiz] = useState(null); 
  const [draftBriefing, setDraftBriefing] = useState(null); 
  const [reviewTab, setReviewTab] = useState("quiz"); 
  const [regeneratingIndex, setRegeneratingIndex] = useState(null); 
  const [isRegeneratingBriefing, setIsRegeneratingBriefing] = useState(false);
  const [deletingQuizId, setDeletingQuizId] = useState(null); 
  const [deletingSubData, setDeletingSubData] = useState(null);

  // Course Management State
  const [newCourseName, setNewCourseName] = useState("");
  const [newCourseStudentCount, setNewCourseStudentCount] = useState("");
  const [isManagingCourses, setIsManagingCourses] = useState(false);
  const [deletingCourseId, setDeletingCourseId] = useState(null);

  // Course Edit Suite State
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [editCourseName, setEditCourseName] = useState("");
  const [editCourseRoster, setEditCourseRoster] = useState([]);
  const [newStudentCallsign, setNewStudentCallsign] = useState("");
  const [isSavingCourse, setIsSavingCourse] = useState(false);

  // Topic Management State
  const [newTopicName, setNewTopicName] = useState("");
  const [isManagingTopics, setIsManagingTopics] = useState(false);

  // --- Student Taking State ---
  const [studentName, setStudentName] = useState("");
  const [pendingStudentName, setPendingStudentName] = useState("");
  const [nameError, setNameError] = useState(""); 
  const [quizStartTime, setQuizStartTime] = useState(null); 
  const [studentAnswers, setStudentAnswers] = useState({});
  const [studentScore, setStudentScore] = useState(null);
  const [isExitingQuiz, setIsExitingQuiz] = useState(false); 
  const [printError, setPrintError] = useState(""); 
  const [courseFilter, setCourseFilter] = useState("All"); 
  const [analyticsRange, setAnalyticsRange] = useState("all"); 
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [activeQuizTimeLeft, setActiveQuizTimeLeft] = useState(null);
  const [isInjectingMockData, setIsInjectingMockData] = useState(false);
  const [customLogoUrl, setCustomLogoUrl] = useState(null);
  const [isWindowFocused, setIsWindowFocused] = useState(true);
  const [showWaitingRoomBriefing, setShowWaitingRoomBriefing] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const autoSubmitLockRef = useRef(false);

  // --- Anti-Screenshot / Focus Loss Detection ---
  useEffect(() => {
    const onFocus = () => setIsWindowFocused(true);
    const onBlur = () => setIsWindowFocused(false);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // --- Detailed Audit State ---
  const [auditingSubId, setAuditingSubId] = useState(null);
  const [auditingQuizId, setAuditingQuizId] = useState(null);

  const fileInputRef = useRef(null);
  const initialLoadRef = useRef(true);
  const submitLockRef = useRef(false);

  const isInstructor = user && user.providerData && user.providerData.length > 0;
  const isSandboxEnv = window.location.hostname === '' || window.location.hostname.includes('googleusercontent') || window.location.protocol === 'about:';

  // --- Derived State Computations ---
  const targetTeacherId = viewingAdminId || user?.uid;

  // The true Student View quiz (without answers)
  const activeQuiz = rawAllQuizzes.find(q => q.id === activeQuizId);

  // The Instructor View quizzes (dynamically re-merged with the secure Answer Vault keys)
  const allQuizzes = rawAllQuizzes.map(quiz => {
      const keys = allQuizKeys[quiz.id];
      if (!keys) return quiz; 
      return {
          ...quiz,
          questions: quiz.questions.map((q, idx) => ({
              ...q,
              answer: keys[idx]?.answer || q.answer,
              explanation: keys[idx]?.explanation || q.explanation
          }))
      };
  });

  const myQuizzes = allQuizzes
    .filter(q => q.teacherId === targetTeacherId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());


  // --- Logo Upload Handler ---
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 300;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/png', 0.8);
        
        setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'branding'), { logo: dataUrl }, { merge: true })
          .catch(err => console.error("Failed to save logo:", err));
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // --- Shared Badge Engine ---
  const calculateCourseStats = (targetCourseName) => {
    if (!targetCourseName || targetCourseName === "Uncategorized" || targetCourseName === "All") return { badges: {}, leaderboard: [] };

    let courseQuizzes = allQuizzes.filter(q => q.course === targetCourseName);
    courseQuizzes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    const virtualSubmissions = [];
    const now = new Date();
    courseQuizzes.forEach(quiz => {
      if (quiz.expiresAt && now > new Date(quiz.expiresAt) && quiz.roster) {
        quiz.roster.forEach(studentName => {
          const hasSubmitted = allSubmissions.some(s => s.quizId === quiz.id && s.studentName.toLowerCase() === studentName.toLowerCase());
          if (!hasSubmitted) {
            virtualSubmissions.push({
              id: `virtual-${quiz.id}-${studentName}`,
              quizId: quiz.id, studentName: studentName, score: 0, durationMs: null, correctCount: 0, totalQuestions: quiz.questions?.length || 0,
              answers: {}, submittedAt: quiz.expiresAt, isVirtual: true 
            });
          }
        });
      }
    });

    const courseSubmissions = [...allSubmissions, ...virtualSubmissions].filter(s => courseQuizzes.some(q => q.id === s.quizId));

    const studentStats = {};
    const courseObj = allCourses.find(c => c.name === targetCourseName);
    
    if (courseObj && courseObj.roster) {
      courseObj.roster.forEach(name => {
        studentStats[name.toLowerCase()] = { name, submissions: [], avgScore: 0, topicStats: {}, totalPoints: 0, firstFinishCount: 0 };
      });
    }

    courseSubmissions.forEach(sub => {
       const lowerName = sub.studentName.toLowerCase();
       if (!studentStats[lowerName]) {
           studentStats[lowerName] = { name: sub.studentName, submissions: [], avgScore: 0, topicStats: {}, totalPoints: 0, firstFinishCount: 0 };
       }
       studentStats[lowerName].submissions.push(sub);
    });

    courseQuizzes.forEach(quiz => {
      const quizSubs = courseSubmissions.filter(s => s.quizId === quiz.id);

      const actualSubs = quizSubs.filter(s => !s.isVirtual && s.submittedAt);
      if (actualSubs.length > 0) {
        const firstSub = [...actualSubs].sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime())[0];
        const lowerName = firstSub.studentName.toLowerCase();
        if (studentStats[lowerName]) {
          studentStats[lowerName].firstFinishCount++;
        }
      }

      const sortedQuizSubs = [...quizSubs].sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const timeA = a.submittedAt ? new Date(a.submittedAt).getTime() : Infinity;
        const timeB = b.submittedAt ? new Date(b.submittedAt).getTime() : Infinity;
        if (timeA !== timeB) return timeA - timeB;
        return (a.durationMs || Infinity) - (b.durationMs || Infinity);
      });

      const quizSize = (quiz.roster && quiz.roster.length > 0) ? quiz.roster.length : sortedQuizSubs.length;

      let rank = 1;
      sortedQuizSubs.forEach(sub => {
        const lowerName = sub.studentName.toLowerCase();
        if (studentStats[lowerName] && !sub.isVirtual) {
          const points = Math.max(0, quizSize - rank + 1);
          studentStats[lowerName].totalPoints += points;
          rank++;
        }
      });
    });

    Object.values(studentStats).forEach(st => {
       const stSubs = [...st.submissions].sort((a,b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
       
       if (stSubs.length > 0) {
         st.avgScore = Math.round(stSubs.reduce((sum, s) => sum + s.score, 0) / stSubs.length);
       }

       let totalMs = 0;
       let totalQs = 0;
       stSubs.forEach(s => {
         if (s.durationMs) { totalMs += s.durationMs; totalQs += s.totalQuestions; }
         const q = courseQuizzes.find(q => q.id === s.quizId);
         if (q && q.questions) {
           q.questions.forEach((question, idx) => {
             const rawTopic = question.topic || "Uncategorized";
             const tName = String(rawTopic).trim().replace(/[\.\s]+$/, '');
             if (!st.topicStats[tName]) st.topicStats[tName] = { correct: 0, total: 0 };
             st.topicStats[tName].total++;
             
             const isNaturallyCorrect = String(s.answers?.[idx] || "").trim().toLowerCase() === String(question.answer || "").trim().toLowerCase();
             const isForcedCorrect = s.overrides && s.overrides.includes(idx);
             const isForcedIncorrect = s.forcedIncorrect && s.forcedIncorrect.includes(idx);
             const isCorrect = isForcedCorrect || (isNaturallyCorrect && !isForcedIncorrect);
             
             if (isCorrect) {
               st.topicStats[tName].correct++;
             }
           });
         }
       });
       
       st.avgTimePerQuestionMs = totalQs > 0 ? Math.round(totalMs / totalQs) : Infinity;
    });

    const badges = {}; 
    const stList = Object.values(studentStats);

    let highestPoints = -1;
    stList.forEach(st => { if (st.totalPoints > highestPoints) highestPoints = st.totalPoints; });
    if (highestPoints > 0) {
      const pointLeaders = stList.filter(st => st.totalPoints === highestPoints);
      pointLeaders.forEach(pl => {
        if(!badges[pl.name.toLowerCase()]) badges[pl.name.toLowerCase()] = [];
        badges[pl.name.toLowerCase()].push({ id: 'pointleader', icon: '🏆', label: 'Point Leader', studentName: pl.name, colorClass: 'bg-orange-50 text-orange-700 border-orange-200' });
      });
    }

    let highestAvg = -1;
    stList.forEach(st => { if (st.submissions.length > 0 && st.avgScore > highestAvg) { highestAvg = st.avgScore; } });
    if (highestAvg !== -1) {
      const academicAces = stList.filter(st => st.submissions.length > 0 && st.avgScore === highestAvg);
      academicAces.forEach(aa => {
        if(!badges[aa.name.toLowerCase()]) badges[aa.name.toLowerCase()] = [];
        badges[aa.name.toLowerCase()].push({ id: 'academicace', icon: '🎓', label: 'Academic Ace', studentName: aa.name, colorClass: 'bg-yellow-50 text-yellow-700 border-yellow-200' });
      });
    }

    let lowestTimePerQ = Infinity;
    stList.forEach(st => { if (st.submissions.length > 0 && st.avgTimePerQuestionMs < lowestTimePerQ) { lowestTimePerQ = st.avgTimePerQuestionMs; } });
    if (lowestTimePerQ < Infinity) {
      const fastPaces = stList.filter(st => st.submissions.length > 0 && st.avgTimePerQuestionMs === lowestTimePerQ);
      fastPaces.forEach(fp => {
        if(!badges[fp.name.toLowerCase()]) badges[fp.name.toLowerCase()] = [];
        badges[fp.name.toLowerCase()].push({ id: 'fastpace', icon: '⚡', label: 'Fastest Pace', studentName: fp.name, colorClass: 'bg-cyan-50 text-cyan-700 border-cyan-200' });
      });
    }

    const allTopicsSet = new Set();
    stList.forEach(st => Object.keys(st.topicStats).forEach(t => allTopicsSet.add(t)));

    allTopicsSet.forEach(topic => {
      if (topic === "Uncategorized") return;
      let highestTopicAvg = -1;
      
      stList.forEach(st => {
        const stats = st.topicStats[topic];
        if (stats && stats.total >= 3) {
          const avg = Math.round((stats.correct / stats.total) * 100);
          if (avg > highestTopicAvg) highestTopicAvg = avg;
        }
      });

      if (highestTopicAvg >= 80) {
        let specialists = stList.filter(st => {
          const stats = st.topicStats[topic];
          if (!stats || stats.total < 3) return false;
          const avg = Math.round((stats.correct / stats.total) * 100);
          return avg === highestTopicAvg;
        });

        // --- TIE-BREAKER: Prioritize highest overall score, then fastest pace ---
        if (specialists.length > 1) {
          specialists.sort((a, b) => {
            if (b.avgScore !== a.avgScore) return b.avgScore - a.avgScore; // Highest overall avg first
            return (a.avgTimePerQuestionMs || Infinity) - (b.avgTimePerQuestionMs || Infinity); // Lowest time first
          });
          specialists = [specialists[0]]; // Exclusively award to the absolute best
        }

        specialists.forEach(spec => {
          if(!badges[spec.name.toLowerCase()]) badges[spec.name.toLowerCase()] = [];
          badges[spec.name.toLowerCase()].push({ 
            id: `spec-${topic.replace(/\s+/g, '-')}`, 
            icon: '🧠', 
            label: `Specialist: ${topic}`, 
            studentName: spec.name,
            colorClass: 'bg-purple-50 text-purple-700 border-purple-200' 
          });
        });
      }
    });

    let highestFirstFinishes = 0;
    stList.forEach(st => { if (st.firstFinishCount > highestFirstFinishes) highestFirstFinishes = st.firstFinishCount; });
    if (highestFirstFinishes > 0) {
      const earlyBirds = stList.filter(st => st.firstFinishCount === highestFirstFinishes);
      earlyBirds.forEach(eb => {
        if(!badges[eb.name.toLowerCase()]) badges[eb.name.toLowerCase()] = [];
        badges[eb.name.toLowerCase()].push({ 
          id: 'earlybird', 
          icon: '🦆', 
          label: 'Early Bird', 
          studentName: eb.name, 
          colorClass: 'bg-white text-slate-800 border-slate-300 shadow-sm' 
        });
      });
    }

    // --- Badge Consolidation (Saves UI Space) ---
    Object.keys(badges).forEach(studentKey => {
      const studentBadges = badges[studentKey];
      const specBadges = studentBadges.filter(b => b.id.startsWith('spec-'));
      
      if (specBadges.length > 1) {
        // If they have multiple, replace them with a single combined badge
        badges[studentKey] = studentBadges.filter(b => !b.id.startsWith('spec-'));
        const topics = specBadges.map(b => b.label.replace('Specialist: ', '')).join(', ');
        badges[studentKey].push({
          id: 'multi-spec',
          icon: '🧠',
          label: `${specBadges.length}X SPECIALIST`,
          title: `Top performer in: ${topics}`,
          studentName: specBadges[0].studentName,
          colorClass: 'bg-purple-50 text-purple-700 border-purple-200 cursor-help'
        });
      } else if (specBadges.length === 1) {
        // If they have just 1, shorten the label
        const badgeIndex = badges[studentKey].findIndex(b => b.id === specBadges[0].id);
        if (badgeIndex !== -1) {
          const topicName = badges[studentKey][badgeIndex].label.replace('Specialist: ', '');
          badges[studentKey][badgeIndex].label = topicName;
          badges[studentKey][badgeIndex].title = `Specialist: ${topicName}`;
          badges[studentKey][badgeIndex].colorClass += ' cursor-help';
        }
      }
      
      // Ensure all badges have a title for hover tooltips
      badges[studentKey].forEach(b => {
          if (!b.title) b.title = b.label;
      });
    });

    const leaderboard = stList.sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
      if (b.avgScore !== a.avgScore) return b.avgScore - a.avgScore;
      return (a.avgTimePerQuestionMs || Infinity) - (b.avgTimePerQuestionMs || Infinity);
    });

    return { badges, leaderboard };
  };

  useEffect(() => {
    if (activeQuiz && activeQuiz.startsAt) {
      const targetTime = new Date(activeQuiz.startsAt).getTime();
      const updateTimer = () => {
        const now = Date.now();
        const diff = targetTime - now;
        if (diff > 0) {
          setTimeRemaining(diff);
        } else {
          setTimeRemaining(null);
        }
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setTimeRemaining(null);
    }
  }, [activeQuiz]);

  useEffect(() => {
    if (activeQuiz && view === 'student_quiz' && !studentScore && quizStartTime) {
      const targetTimeByLimit = activeQuiz.timeLimit ? (quizStartTime + (activeQuiz.timeLimit * 60 * 1000)) : Infinity;
      const targetTimeByExpiry = activeQuiz.expiresAt ? new Date(activeQuiz.expiresAt).getTime() : Infinity;
      
      const finalTargetTime = Math.min(targetTimeByLimit, targetTimeByExpiry);
      
      if (finalTargetTime === Infinity) {
        setActiveQuizTimeLeft(null);
        return;
      }
      
      const updateTimer = () => {
        const now = Date.now();
        const left = finalTargetTime - now;
        if (left <= 0) {
          setActiveQuizTimeLeft(0);
        } else {
          setActiveQuizTimeLeft(left);
        }
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setActiveQuizTimeLeft(null);
    }
  }, [activeQuiz, quizStartTime, view, studentScore]);

  useEffect(() => {
    if (activeQuizTimeLeft === 0 && view === 'student_quiz' && !studentScore && !isExitingQuiz && !autoSubmitLockRef.current) {
      autoSubmitLockRef.current = true;
      handleStudentSubmit(activeQuiz);
    }
  }, [activeQuizTimeLeft, view, studentScore, isExitingQuiz, activeQuiz]);

  // --- 1. Authentication & State Tracking ---
  useEffect(() => {
    const initAuth = async () => {
      try {
        setAuthErrorMsg("");
        if (isSignInWithEmailLink(auth, window.location.href)) {
          let email = window.localStorage.getItem('emailForSignIn');
          if (!email) {
            email = window.prompt('Please provide your email for confirmation');
          }
          if (email) {
            await signInWithEmailLink(auth, email, window.location.href);
            window.localStorage.removeItem('emailForSignIn');
            window.history.replaceState(null, null, window.location.pathname);
            setView("dashboard");
            return;
          }
        }
        if (!auth.currentUser) {
          if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
            await signInWithCustomToken(auth, __initial_auth_token);
          } else {
            await signInAnonymously(auth);
          }
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        setAuthErrorMsg(err.message);
      } finally {
        setIsAuthLoading(false);
      }
    };
    
    initAuth();

    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setIsAuthLoading(false);
      const currentlyInstructor = u && u.providerData && u.providerData.length > 0;
      if (currentlyInstructor && view === "home" && initialLoadRef.current) {
        setView("dashboard");
      }
      initialLoadRef.current = false;
    });

    return () => unsubscribe();
  }, [view]);

  // --- 2. Firestore Subscriptions ---
  useEffect(() => {
    if (!user) return;

    // Fetch Public Quiz Sheets
    const quizzesRef = collection(db, 'artifacts', appId, 'public', 'data', 'quizzes');
    const unsubQuizzes = onSnapshot(quizzesRef, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRawAllQuizzes(data);
    }, (err) => console.error("Quizzes fetch error:", err));

    // Fetch Submissions
    const submissionsRef = collection(db, 'artifacts', appId, 'public', 'data', 'submissions');
    const unsubSubmissions = onSnapshot(submissionsRef, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllSubmissions(data);
    }, (err) => console.error("Submissions fetch error:", err));

    let unsubCourses = () => {};
    let unsubTopics = () => {};
    let unsubKeys = () => {};
    const activeTeacherId = viewingAdminId || user?.uid;

    if (activeTeacherId) {
      // Securely fetch Answer Keys (Only Instructors / Admins should be able to read this due to DB rules)
      const keysRef = collection(db, 'artifacts', appId, 'public', 'data', 'quiz_keys');
      unsubKeys = onSnapshot(keysRef, (snapshot) => {
        const data = {};
        snapshot.docs.forEach(doc => {
           data[doc.id] = doc.data().keys;
        });
        setAllQuizKeys(data);
      }, (err) => console.error("Keys fetch error:", err));

      const coursesRef = collection(db, 'artifacts', appId, 'users', activeTeacherId, 'courses');
      unsubCourses = onSnapshot(coursesRef, (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllCourses(data.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()));
      }, (err) => console.error("Courses fetch error:", err));

      const topicsRef = collection(db, 'artifacts', appId, 'users', activeTeacherId, 'topics');
      unsubTopics = onSnapshot(topicsRef, (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllTopics(data.sort((a, b) => a.name.localeCompare(b.name)));
      }, (err) => console.error("Topics fetch error:", err));
    }

    const brandingRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'branding');
    const unsubBranding = onSnapshot(brandingRef, (docSnap) => {
      if (docSnap.exists() && docSnap.data().logo) {
        setCustomLogoUrl(docSnap.data().logo);
      } else {
        setCustomLogoUrl(null);
      }
    }, (err) => console.error("Branding fetch error:", err));

    return () => {
      unsubQuizzes();
      unsubSubmissions();
      unsubCourses();
      unsubTopics();
      unsubKeys();
      unsubBranding();
    };
  }, [user, viewingAdminId]);

  useEffect(() => {
    if (user && !user.isAnonymous && !isReadOnly) {
      const keyDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'sharedKeys', user.uid);
      const unsub = onSnapshot(keyDocRef, (docSnap) => {
        if (docSnap.exists()) {
           setAdminAuditKey(docSnap.data().key);
           setAdminKeyHistory(docSnap.data().loginHistory || []);
        } else {
           setAdminAuditKey(null);
           setAdminKeyHistory([]);
        }
      }, (err) => console.error("Error fetching admin key:", err));
      return () => unsub();
    }
  }, [user, isReadOnly]);

  const handleGoogleLogin = async () => {
    setIsLoggingIn(true);
    setLoginError("");
    try {
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, googleProvider);
      setShowAdminPanel(false);
      setView("dashboard");
    } catch (err) {
      console.error("Login failed:", err);
      if (err.code === 'auth/unauthorized-domain') {
        setLoginError("Domain not authorized in Firebase. Please add this URL to your Firebase Console or use Email Login.");
      } else {
        setLoginError(`Google sign-in failed: ${err.message}`);
      }
      setIsLoggingIn(false);
    }
  };

  const handleSendEmailLink = async () => {
    if (!loginEmail) return;
    setIsLoggingIn(true);
    setLoginError("");
    try {
      const currentHost = window.location.hostname;
      const isAuthorizedHost = currentHost === 'docuquiz-3f011.web.app' || currentHost === 'docuquiz-3f011.firebaseapp.com' || currentHost === 'localhost';
      const redirectUrl = isAuthorizedHost ? window.location.href : "https://docuquiz-3f011.web.app";

      const actionCodeSettings = {
        url: redirectUrl, 
        handleCodeInApp: true,
      };
      
      await sendSignInLinkToEmail(auth, loginEmail, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', loginEmail);
      setEmailSent(true);
    } catch (err) {
      console.error("Error sending email link:", err);
      setLoginError(`Error: ${err.message}`);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
        await signInWithCustomToken(auth, __initial_auth_token);
      } else {
        await signInAnonymously(auth);
      }
      setView("home");
      setShowAdminPanel(false);
      setIsManagingCourses(false);
      setCourseFilter("All");
      setLoginEmail("");
      setEmailSent(false);
      setIsReadOnly(false);
      setViewingAdminId(null);
      setAuditKeyInput("");
      setAuditingSubId(null);
      setAuditingQuizId(null);
    } catch (err) { console.error("Logout failed:", err); }
  };

  const generateNewAuditKey = async () => {
    if (!user || user.isAnonymous || isReadOnly) return;
    try {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let newKey = '';
      for (let i = 0; i < 20; i++) newKey += chars.charAt(Math.floor(Math.random() * chars.length));
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'sharedKeys', user.uid), {
        key: newKey, teacherId: user.uid, updatedAt: new Date().toISOString(), loginHistory: []
      });
    } catch (err) { console.error("Error generating audit key:", err); }
  };

  const handleAuditorLogin = async () => {
    if (!auditKeyInput.trim()) { setLoginError("Please enter an access key."); return; }
    setLoginError("");
    try {
      let currentUser = auth.currentUser;
      if (!currentUser) {
          const cred = await signInAnonymously(auth);
          currentUser = cred.user;
      }
      const keysQuery = query(collection(db, 'artifacts', appId, 'public', 'data', 'sharedKeys'), where("key", "==", auditKeyInput.trim()));
      const keySnapshot = await getDocs(keysQuery);
      if (!keySnapshot.empty) {
        const keyDoc = keySnapshot.docs[0];
        const keyData = keyDoc.data();
        
        const history = keyData.loginHistory || [];
        history.push(new Date().toISOString());
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'sharedKeys', keyDoc.id), {
           loginHistory: history
        });

        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'auditor_sessions', currentUser.uid), {
           key: keyData.key,
           teacherId: keyData.teacherId,
           createdAt: new Date().toISOString()
        });

        setViewingAdminId(keyData.teacherId);
        setIsReadOnly(true);
        setView('dashboard');
        setAuditKeyInput("");
      } else {
        setLoginError("Invalid or expired access key.");
      }
    } catch (err) { console.error("Auditor login error:", err); setLoginError("Error connecting to server."); }
  };

  const handleAddCourse = async () => {
    if (!newCourseName.trim() || !user || user.isAnonymous) return;
    const val = parseInt(newCourseStudentCount);
    let newRoster = [];
    if (!isNaN(val) && val > 0) {
      const nato = ['Alpha', 'Bravo', 'Charlie', 'Delta', 'Echo', 'Foxtrot', 'Golf', 'Hotel', 'India', 'Juliet', 'Kilo', 'Lima', 'Mike', 'November', 'Oscar', 'Papa', 'Quebec', 'Romeo', 'Sierra', 'Tango', 'Uniform', 'Victor', 'Whiskey', 'X-ray', 'Yankee', 'Zulu'];
      for (let i = 0; i < val; i++) {
        const baseName = nato[i % 26];
        const suffix = i >= 26 ? ` ${Math.floor(i / 26) + 1}` : '';
        newRoster.push(`${baseName}${suffix}`);
      }
    }
    try {
      await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'courses'), {
        name: newCourseName.trim(), roster: newRoster, lifetimeQuizCount: 0, createdAt: new Date().toISOString()
      });
      setNewCourseName("");
      setNewCourseStudentCount("");
    } catch (err) { console.error("Failed to add course:", err); }
  };

  const handleRemoveCourse = async (courseId, courseNameToDelete) => {
    if (!user || user.isAnonymous) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'courses', courseId));
      const quizzesToDelete = allQuizzes.filter(q => q.teacherId === user.uid && q.course === courseNameToDelete);
      for (const quiz of quizzesToDelete) {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'quizzes', quiz.id));
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'quiz_keys', quiz.id));
        const submissionsToDelete = allSubmissions.filter(s => s.quizId === quiz.id);
        for (const sub of submissionsToDelete) {
          await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'submissions', sub.id));
        }
      }
      if (courseFilter === courseNameToDelete) setCourseFilter("All");
    } catch (err) { console.error("Failed to delete course and associated data:", err); } 
    finally { setDeletingCourseId(null); }
  };

  const handleStartEditCourse = (course) => {
    setEditingCourseId(course.id);
    setEditCourseName(course.name);
    setEditCourseRoster([...(course.roster || [])]);
    setNewStudentCallsign("");
  };

  const handleAddStudentToEdit = () => {
    const trimmed = newStudentCallsign.trim();
    const formatted = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    if (formatted && !editCourseRoster.some(s => s.toLowerCase() === formatted.toLowerCase())) {
      setEditCourseRoster([...editCourseRoster, formatted]);
      setNewStudentCallsign("");
    }
  };

  const handleRemoveStudentFromEdit = (studentToRemove) => {
    setEditCourseRoster(editCourseRoster.filter(s => s !== studentToRemove));
  };

  const handleSaveCourseEdit = async (courseId, originalName) => {
    if (!user || user.isAnonymous) return;
    setIsSavingCourse(true);
    try {
      const batch = writeBatch(db);
      const newName = editCourseName.trim() || "Uncategorized";

      const courseRef = doc(db, 'artifacts', appId, 'users', user.uid, 'courses', courseId);
      batch.update(courseRef, {
        name: newName,
        roster: editCourseRoster
      });

      if (newName !== originalName) {
        const quizzesToUpdate = allQuizzes.filter(q => q.teacherId === user.uid && q.course === originalName);
        quizzesToUpdate.forEach(q => {
          const qRef = doc(db, 'artifacts', appId, 'public', 'data', 'quizzes', q.id);
          const updatedTitle = q.title ? q.title.replace(originalName, newName) : q.title;
          batch.update(qRef, { course: newName, title: updatedTitle });
        });
      }

      await batch.commit();

      if (courseFilter === originalName) {
        setCourseFilter(newName);
      }
      setEditingCourseId(null);
    } catch (err) {
      console.error("Failed to save course edits:", err);
      alert("Failed to save changes.");
    } finally {
      setIsSavingCourse(false);
    }
  };

  const handleAddTopic = async () => {
    if (!newTopicName.trim() || !user || user.isAnonymous) return;
    try {
      await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'topics'), {
        name: newTopicName.trim(), createdAt: new Date().toISOString()
      });
      setNewTopicName("");
    } catch (err) { console.error("Failed to add topic:", err); }
  };

  const handleRemoveTopic = async (topicId) => {
    if (!user || user.isAnonymous) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'topics', topicId));
      const deletedTopic = allTopics.find(t => t.id === topicId);
      if (deletedTopic) setSelectedTopics(prev => prev.filter(t => t !== deletedTopic.name));
    } catch (err) { console.error("Failed to delete topic:", err); }
  };

  const loadStandardTopics = async () => {
    if (!user || (user.isAnonymous && !viewingAdminId)) return;
    const defaults = ["Aerodynamics", "Meteorology / Weather", "Navigation", "Air Law & Regulations", "Aircraft Systems", "Flight Instruments", "Human Performance", "Radio Telephony / Comms", "Weight & Balance", "Flight Planning", "Emergency Procedures", "Aircraft Limitations"];
    try {
      const targetTeacherId = viewingAdminId || user.uid;
      const batch = defaults.map(t => addDoc(collection(db, 'artifacts', appId, 'users', targetTeacherId, 'topics'), { name: t, createdAt: new Date().toISOString() }));
      await Promise.all(batch);
    } catch (err) { console.error("Failed to load default topics:", err); }
  };

  const toggleTopicSelection = (topicName) => {
    setSelectedTopics(prev => prev.includes(topicName) ? prev.filter(t => t !== topicName) : [...prev, topicName]);
  };

  useEffect(() => {
    const loadPdfJs = async () => {
      if (window.pdfjsLib) return;
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.async = true;
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      };
      document.body.appendChild(script);
    };
    loadPdfJs();
  }, []);

  const difficultyLabels = { 1: "Beginner", 2: "Easy", 3: "Medium", 4: "Hard", 5: "Expert" };

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile || uploadedFile.type !== "application/pdf") return;
    if (!window.pdfjsLib) return setStatus("error") || setStatusMessage("Library loading...");

    setFile(uploadedFile);
    setStatus("extracting");
    setStatusMessage("Extracting text from PDF...");

    try {
      const arrayBuffer = await uploadedFile.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = "";
      const maxPages = Math.min(pdf.numPages, 100); 

      for (let i = 1; i <= maxPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        fullText += textContent.items.map(item => item.str).join(" ") + "\n";
      }

      if (fullText.length > 200000) fullText = fullText.substring(0, 200000) + "...";
      setPdfText(fullText);
      setStatus("idle");
      setStatusMessage("");
    } catch (err) {
      setStatus("error");
      setStatusMessage("Failed to parse PDF.");
    }
  };

  const generateQuiz = async () => {
    const isPdfMode = !!pdfText;
    if ((!isPdfMode && selectedTopics.length === 0 && !topicFocus.trim()) || !user) return;
    setStatus("generating");
    setStatusMessage("Generating questions with AI...");

    if (manualAiMode && !canvasApiKey.trim()) {
      setStatus("error");
      setStatusMessage("Please paste your Pay-As-You-Go API key into the yellow box.");
      return;
    }

    const topicsStr = selectedTopics.length > 0 ? `\nCRITICAL TOPICS TO COVER (You MUST map each question to one of these exact topics): ${selectedTopics.join(", ")}.` : "";
    const instructionsStr = topicFocus.trim() ? `\nINSTRUCTOR'S SPECIFIC INSTRUCTIONS: ${topicFocus.trim()}` : "";

    let questionsPrompt = "";
    if (isPdfMode) {
      questionsPrompt = `You are an expert Aviation Instructor. Design ${numQuestions} insightful, rigorous multiple-choice questions (Difficulty: ${difficultyLabels[difficulty]}) strictly based on this text. Do not ask shallow trivia. Focus on practical application, deep understanding, and critical aviation concepts found in the text. Each question must have exactly ${numOptions} options, and the correct answer must perfectly match one of them. You MUST assign a specific 'topic' to every single question.\nLanguage Requirement: Whenever you use a difficult, complex, or highly technical aviation term in the question, the options, or the explanation, you MUST immediately provide a brief UAE Arabic translation in brackets right next to the English word (e.g., 'Angle of Attack [زاوية الهجوم]'). Keep the primary language in simple, plain English.${topicsStr}${instructionsStr}\n\nSource Text: """${pdfText}"""`;
    } else {
      questionsPrompt = `You are an expert Aviation Instructor. Design ${numQuestions} insightful, rigorous multiple-choice questions (Difficulty: ${difficultyLabels[difficulty]}). Do not ask shallow trivia. Focus on practical application, deep understanding, and standard industry practices (ICAO, FAA, EASA). Each question must have exactly ${numOptions} options, and the correct answer must perfectly match one of them. You MUST assign a specific 'topic' to every single question.\nLanguage Requirement: Whenever you use a difficult, complex, or highly technical aviation term in the question, the options, or the explanation, you MUST immediately provide a brief UAE Arabic translation in brackets right next to the English word (e.g., 'Angle of Attack [زاوية الهجوم]'). Keep the primary language in simple, plain English.${topicsStr}${instructionsStr}`;
    }
    
    const questionsSchema = {
      type: "OBJECT",
      properties: {
        questions: {
          type: "ARRAY",
          items: {
            type: "OBJECT",
            properties: {
              question: { type: "STRING" },
              options: { type: "ARRAY", items: { type: "STRING" } },
              answer: { type: "STRING" },
              explanation: { type: "STRING" },
              topic: { type: "STRING" }
            },
            required: ["question", "options", "answer", "explanation", "topic"]
          }
        }
      },
      required: ["questions"]
    };

    try {
      let questionsData;

      if (manualAiMode) {
        questionsData = await fetchCanvasGemini(questionsPrompt, questionsSchema, canvasApiKey.trim());
      } else {
        const generateData = httpsCallable(cloudFunctions, 'generateQuizData');
        const result = await generateData({ action: "generate_questions", payload: { prompt: questionsPrompt, schema: questionsSchema } });
        questionsData = { questions: result.data.questions };
      }

      const randomizedQuiz = questionsData.questions.map(q => ({ ...q, options: shuffleArray(q.options) }));
      setDraftBriefing(null);
      setDraftQuiz(randomizedQuiz);
      setStatus("idle");
      setView("review");
    } catch (err) { setStatus("error"); setStatusMessage("Failed to generate: " + err.message); }
  };

  const regenerateSingleQuestion = async (indexToReplace) => {
    const isPdfMode = !!pdfText;
    if ((!isPdfMode && selectedTopics.length === 0 && !topicFocus.trim()) || !draftQuiz) return;
    setRegeneratingIndex(indexToReplace);
    const oldQuestion = draftQuiz[indexToReplace].question;
    const topicsStr = selectedTopics.length > 0 ? `\nCRITICAL TOPICS TO COVER: ${selectedTopics.join(", ")}.` : "";
    const instructionsStr = topicFocus.trim() ? `\nINSTRUCTOR'S SPECIFIC INSTRUCTIONS: ${topicFocus.trim()}` : "";

    let prompt = "";
    if (isPdfMode) {
      prompt = `You are an expert Aviation Instructor. Create exactly ONE multiple-choice question strictly based on this text.${topicsStr}${instructionsStr}\nRequirements:\n1. Design an insightful, rigorous question (Difficulty: ${difficultyLabels[difficulty]}). Do not ask shallow trivia. Focus on deep understanding.\n2. Exactly ${numOptions} options.\n3. The answer MUST perfectly match one of the options. Use standard ICAO/FAA/EASA terminology.\n4. It MUST BE DIFFERENT from this existing question: "${oldQuestion}"\n5. You MUST assign a specific 'topic' to the question. If a list of CRITICAL TOPICS is provided, use ONLY topics from that list.\n6. Language Requirement: Whenever you use a difficult, complex, or highly technical aviation term in the question, the options, or the explanation, you MUST immediately provide a brief UAE Arabic translation in brackets right next to the English word (e.g., 'Angle of Attack [زاوية الهجوم]'). Keep the primary language in simple, plain English.\n\nSource Text: """${pdfText}"""`;
    } else {
      prompt = `You are an expert Aviation Instructor. Create exactly ONE multiple-choice question.${topicsStr}${instructionsStr}\nRequirements:\n1. Design an insightful, rigorous question (Difficulty: ${difficultyLabels[difficulty]}). Do not ask shallow trivia. Focus on deep understanding.\n2. Exactly ${numOptions} options.\n3. Limit scope strictly to Aviation.\n4. The answer MUST perfectly match one of the options.5. It MUST BE DIFFERENT from this existing question: "${oldQuestion}"\n6. You MUST assign a specific 'topic' to the question. If a list of CRITICAL TOPICS is provided, use ONLY topics from that list.\n7. Language Requirement: Whenever you use a difficult, complex, or highly technical aviation term in the question, the options, or the explanation, you MUST immediately provide a brief UAE Arabic translation in brackets right next to the English word (e.g., 'Angle of Attack [زاوية الهجوم]'). Keep the primary language in simple, plain English.`;
    }

    const schemaDefinition = {
      type: "ARRAY",
      items: {
        type: "OBJECT",
        properties: { question: { type: "STRING" }, options: { type: "ARRAY", items: { type: "STRING" } }, answer: { type: "STRING" }, explanation: { type: "STRING" }, topic: { type: "STRING" } },
        required: ["question", "options", "answer", "explanation", "topic"]
      }
    };

    try {
      let newQuestion;
      if (manualAiMode) {
         if (!canvasApiKey.trim()) throw new Error("Please paste your Pay-As-You-Go API key in Setup to regenerate.");
         const result = await fetchCanvasGemini(prompt, schemaDefinition, canvasApiKey.trim());
         newQuestion = Array.isArray(result) ? result[0] : result;
      } else {
        const generateData = httpsCallable(cloudFunctions, 'generateQuizData');
        const result = await generateData({ action: "regenerate_question", payload: { prompt, schema: schemaDefinition } });
        newQuestion = result.data.question || result.data[0];
      }
      if (newQuestion && newQuestion.question) {
        newQuestion.options = shuffleArray(newQuestion.options);
        setDraftQuiz(prev => { const updated = [...prev]; updated[indexToReplace] = newQuestion; return updated; });
      }
    } catch (err) { console.error("Failed to regenerate question:", err); alert("Failed to regenerate this question. Please try again."); } 
    finally { setRegeneratingIndex(null); }
  };

  const regenerateBriefing = async () => {
    if (!draftQuiz || draftQuiz.length === 0) return;
    setIsRegeneratingBriefing(true);
    const questionTexts = draftQuiz.map((q, idx) => `${idx + 1}. ${q.question} (Correct Answer: ${q.answer})`).join("\n");
    
    const prompt = `You are an expert Aviation Instructor. I have a finalized list of quiz questions.\nYour task is to write a comprehensive 'Pre-Flight Briefing' that acts as a study guide, not a direct answer key.\n\nCRITICAL TEACHING MANDATES:\n1. You must cover the underlying theory for ALL the topics in the quiz.\n2. CONCEALMENT RULE: You should directly reveal the exact answers for approximately half of the questions. For the remaining half, you MUST ONLY teach the broad principles, formulas, or context, forcing the student to think critically to find the right answer on their own.\n3. Keep the explanations in simple, plain English suitable for second-language learners. When introducing a difficult, highly technical aviation concept, provide a brief translation or explanation of that specific concept in brackets using UAE Arabic.\n\nSTRICT NEGATIVE CONSTRAINTS:\n- NEVER write "(Reveal)", "(Conceal)", or reference question numbers (e.g., Q1, Q5) in your output.\n- NEVER copy and paste the verbatim questions or multiple-choice options into the briefing.\n- Write the guide naturally, hiding all internal logic from the student.\n\nSTRICT FORMATTING MANDATE:\n- Organize the briefing into distinct sections. Use Markdown headers (###) for section titles.\n- Use standard Markdown bullet points (-) to break down lists of facts.\n- Use asterisks (**) to bold important keywords.\n- Address the students as "aviators" in the introduction.\n\nHere are the questions the briefing MUST cover and explain:\n${questionTexts}`;

    try {
      let newBriefing;
      if (manualAiMode) {
        if (!canvasApiKey.trim()) throw new Error("Please paste your Pay-As-You-Go API key in Setup to regenerate.");
        newBriefing = await fetchCanvasGemini(prompt, null, canvasApiKey.trim());
      } else {
        const generateData = httpsCallable(cloudFunctions, 'generateQuizData');
        const result = await generateData({ action: "regenerate_briefing", payload: { prompt } });
        newBriefing = result.data.briefing;
      }
      
      if (newBriefing) {
        const richTextHtml = parseMarkdownToHTML(newBriefing);
        setDraftBriefing(richTextHtml);
      }
    } catch (err) { console.error("Failed to regenerate briefing:", err); alert("Failed to sync the briefing. Please try again."); } 
    finally { setIsRegeneratingBriefing(false); }
  };

  const exportQuizData = () => {
    if (!draftQuiz) return;
    const exportPacket = { sourceFile: file?.name || "Imported Assessment", briefing: draftBriefing, questions: draftQuiz, timeLimit: timeLimit };
    const textArea = document.createElement("textarea");
    textArea.value = JSON.stringify(exportPacket);
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopiedId('export-data');
      setTimeout(() => setCopiedId(null), 3000);
    } catch (err) { console.error('Copy failed', err); alert("Failed to copy data."); }
    document.body.removeChild(textArea);
  };

  const handleImportQuiz = () => {
    if (!importText.trim()) return;
    try {
      const parsed = JSON.parse(importText);
      if (parsed && parsed.questions && Array.isArray(parsed.questions) && parsed.questions.length > 0 && parsed.questions[0].question) {
        setFile({ name: parsed.sourceFile || "Imported Assessment" });
        setDraftBriefing(parsed.briefing || null); 
        setDraftQuiz(parsed.questions);
        setTimeLimit(parsed.timeLimit || "");
        setImportText("");
        setStatus("idle");
        setStatusMessage("");
        setView("review");
      } else { throw new Error("Invalid structure"); }
    } catch (err) { setStatus("error"); setStatusMessage("Invalid import data."); }
  };

  // --- THE VAULT METHOD: SECURE QUIZ PUBLISHING ---
  const saveQuizToCloud = async () => {
    if (!draftQuiz || !user || (user.isAnonymous && !viewingAdminId)) return; 
    setStatus("saving");
    const targetTeacherId = viewingAdminId || user.uid;
    try {
      const finalCourseName = courseName.trim() || "Uncategorized";
      let quizTitle = "";
      const targetCourse = allCourses.find(c => c.name === finalCourseName);

      if (targetCourse) {
        const currentCount = targetCourse.lifetimeQuizCount || 0;
        const newCount = currentCount + 1;
        quizTitle = `Quiz-${finalCourseName}-${newCount}`;
        await updateDoc(doc(db, 'artifacts', appId, 'users', targetTeacherId, 'courses', targetCourse.id), { lifetimeQuizCount: newCount });
      } else {
        const existingNumbers = allQuizzes.filter(q => q.teacherId === targetTeacherId && (q.course || "Uncategorized") === finalCourseName).map(q => {
            const escapedName = finalCourseName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
            const match = q.title.match(new RegExp(`Quiz-${escapedName}-(\\d+)`));
            return match ? parseInt(match[1]) : 0;
        });
        const newCount = Math.max(0, ...existingNumbers) + 1;
        quizTitle = `Quiz-${finalCourseName}-${newCount}`;
      }

      let sourceName = "Imported Assessment";
      if (file?.name) sourceName = file.name;
      else if (selectedTopics.length > 0) sourceName = `Topics: ${selectedTopics.slice(0, 2).join(", ")}${selectedTopics.length > 2 ? '...' : ''}`;
      else if (topicFocus.trim()) sourceName = `Custom AI Instructions utilized`;

      // 1. Separate the Answers from the Public Questions
      const publicQuestions = draftQuiz.map(q => {
          const { answer, explanation, ...rest } = q;
          return rest;
      });
      
      const secretKeys = draftQuiz.map(q => ({
          answer: q.answer,
          explanation: q.explanation
      }));

      // 2. Save Public Quiz (No Answers)
      const quizDocRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'quizzes'), {
        title: quizTitle, sourceFile: sourceName, course: finalCourseName, topics: selectedTopics, difficulty: difficultyLabels[difficulty],
        briefing: draftBriefing || "", questions: publicQuestions, roster: selectedStudents, teacherId: targetTeacherId, createdAt: new Date().toISOString(),
        startsAt: startsAt ? new Date(startsAt).toISOString() : null, expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        timeLimit: timeLimit ? parseInt(timeLimit) : null
      });

      // 3. Save Secret Answers to the Vault
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'quiz_keys', quizDocRef.id), {
        keys: secretKeys
      });

      setStatus("idle"); setDraftQuiz(null); setDraftBriefing(null); setFile(null); setPdfText(""); setCourseName(""); setSelectedStudents([]);
      setStartsAt(""); setExpiresAt(""); setTimeLimit(""); setSelectedTopics([]); setTopicFocus(""); setCourseFilter("All"); setView("dashboard");
    } catch (err) { setStatus("error"); setStatusMessage("Failed to save: " + err.message); }
  };

  // --- NEW: SERVER-SIDE SUBMIT AND GRADE ---
  const handleStudentSubmit = async (quiz) => {
    if (submitLockRef.current || isExitingQuiz) return;
    submitLockRef.current = true;
    setIsExitingQuiz(true);
    setSubmitError("");

    if (!studentName.trim()) { 
      submitLockRef.current = false;
      setIsExitingQuiz(false); 
      return; 
    }

    if (isSandboxEnv) {
        setSubmitError("SANDBOX LIMITATION: Secure Server-Side Grading cannot be tested inside the Canvas preview because this Sandbox cannot connect to your backend Cloud Functions. Please test student submissions directly on your live website!");
        submitLockRef.current = false;
        setIsExitingQuiz(false);
        return;
    }
    
    const durationMs = quizStartTime ? Date.now() - quizStartTime : null;

    try {
        // Send raw answers to the backend Cloud Function instead of grading in the browser
        const gradeAssessment = httpsCallable(cloudFunctions, 'gradeAssessment');
        const result = await gradeAssessment({
            quizId: activeQuizId,
            studentName: studentName,
            answers: studentAnswers,
            durationMs: durationMs,
            appId: appId
        });

        // The backend responds with the final score AND the graded explanations
        setStudentScore(result.data);
        setView("student_result");

    } catch (err) {
        console.error("Secure Grading Error:", err);
        setSubmitError(err.message || "Failed to connect to grading server. Please try submitting again or contact your instructor.");
        submitLockRef.current = false;
        setIsExitingQuiz(false);
    }
  };

  const getAppUrl = () => {
    const isSandbox = window.location.hostname === '' || window.location.hostname.includes('googleusercontent') || window.location.protocol === 'about:';
    return isSandbox ? "https://your-future-website.com" : `${window.location.origin}${window.location.pathname}`;
  };

  const copyShareInstructions = (quiz) => {
    const url = getAppUrl();
    const topicsStr = quiz.topics && quiz.topics.length > 0 ? quiz.topics.join(", ") : (quiz.course || "General Aviation");
    
    const formatDateTime = (isoString) => {
      if (!isoString) return null;
      return new Date(isoString).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const startTimeStr = formatDateTime(quiz.startsAt) || "Available Now";
    const expiryTimeStr = formatDateTime(quiz.expiresAt) || "No Expiration";

    const message = `🚁 *RotorShed Assessment Invite*\n\n` +
                    `⏰ *Availability:*\n` +
                    `• Opens: ${startTimeStr}\n` +
                    `• Closes: ${expiryTimeStr}\n\n` +
                    `📋 *Instructions:*\n` +
                    `1. Go to: ${url}\n` +
                    `2. Enter Quiz Code: *${quiz.id}*\n` +
                    `3. Select your assigned Callsign.\n` +
                    `4. Review the Pre-Flight Briefing.\n` +
                    `5. Complete the assessment covering: *${topicsStr}*.`;

    const textArea = document.createElement("textarea");
    textArea.value = message;
    document.body.appendChild(textArea);
    textArea.select();
    try { document.execCommand('copy'); setCopiedId(quiz.id); setTimeout(() => setCopiedId(null), 2000); } 
    catch (err) { console.error('Copy failed', err); }
    document.body.removeChild(textArea);
  };

  const copyAppUrl = () => {
    const url = getAppUrl();
    const textArea = document.createElement("textarea");
    textArea.value = url;
    document.body.appendChild(textArea);
    textArea.select();
    try { document.execCommand('copy'); setCopiedId('url'); setTimeout(() => setCopiedId(null), 2000); } 
    catch (err) { console.error('Copy failed', err); }
    document.body.removeChild(textArea);
  };

  const copyQuizCode = (id) => {
    const textArea = document.createElement("textarea");
    textArea.value = id;
    document.body.appendChild(textArea);
    textArea.select();
    try { document.execCommand('copy'); setCopiedId(`code-${id}`); setTimeout(() => setCopiedId(null), 2000); } 
    catch (err) { console.error('Copy failed', err); }
    document.body.removeChild(textArea);
  };

  const copyAdminKey = () => {
    if (!adminAuditKey) return;
    const textArea = document.createElement("textarea");
    textArea.value = adminAuditKey;
    document.body.appendChild(textArea);
    textArea.select();
    try { document.execCommand('copy'); setCopiedId('admin-key'); setTimeout(() => setCopiedId(null), 2000); } 
    catch (err) { console.error('Copy failed', err); }
    document.body.removeChild(textArea);
  };

  const handleDeleteQuiz = async (quizId) => {
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'quizzes', quizId));
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'quiz_keys', quizId));
      const submissionsToDelete = allSubmissions.filter(s => s.quizId === quizId);
      for (const sub of submissionsToDelete) await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'submissions', sub.id));
    } catch (err) { console.error("Failed to delete quiz:", err); } 
    finally { setDeletingQuizId(null); }
  };

  const formatDuration = (ms) => {
    if (!ms) return "";
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  const formatTimePerQuestion = (ms) => {
    if (!ms) return "N/A";
    const sec = Math.round(ms / 1000);
    return `${sec}s / question`;
  };

  const copyQuizResults = (quiz, submissions) => {
    const sortedSubs = [...submissions].sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const timeA = a.submittedAt ? new Date(a.submittedAt).getTime() : Infinity;
      const timeB = b.submittedAt ? new Date(b.submittedAt).getTime() : Infinity;
      if (timeA !== timeB) return timeA - timeB;
      return (a.durationMs || Infinity) - (b.durationMs || Infinity);
    });

    const quizSize = (quiz.roster && quiz.roster.length > 0) ? quiz.roster.length : sortedSubs.length;
    let text = `🚁 *RotorShed Results: ${quiz.title}*\n\n`;
    
    sortedSubs.forEach((sub, index) => {
      let points = 0;
      if (!sub.isVirtual) {
         points = Math.max(0, quizSize - index);
      }

      if (sub.isVirtual) {
        text += `${index + 1}. ${sub.studentName} - 0% [0 Pts] (Missed Deadline)\n`;
      } else {
        const timeStr = sub.submittedAt 
          ? new Date(sub.submittedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) 
          : "Unknown Time";
        text += `${index + 1}. ${sub.studentName} - ${sub.score}% [+${points} Pts] (✅ ${timeStr})\n`;
      }
    });

    const textArea = document.createElement("textarea");
    textArea.value = text.trim();
    document.body.appendChild(textArea);
    textArea.select();
    try { 
      document.execCommand('copy'); 
      setCopiedId(`results-${quiz.id}`); 
      setTimeout(() => setCopiedId(null), 2000); 
    } 
    catch (err) { console.error('Copy failed', err); }
    document.body.removeChild(textArea);
  };

  const executeDeleteSubmission = async () => {
    if (!deletingSubData || !user || user.isAnonymous || isReadOnly) return;
    try {
      const quizToUpdate = allQuizzes.find(q => q.id === deletingSubData.quizId);
      if (quizToUpdate && quizToUpdate.roster) {
        const updatedRoster = quizToUpdate.roster.filter(n => n.toLowerCase() !== deletingSubData.studentName.toLowerCase());
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'quizzes', deletingSubData.quizId), { roster: updatedRoster });
      }
      if (!deletingSubData.isVirtual) await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'submissions', deletingSubData.subId));
    } catch (err) { console.error("Failed to delete submission:", err); } 
    finally { setDeletingSubData(null); }
  };

  // --- Specific Question Override Engine ---
  const handleOverrideQuestion = async (sub, qIdx, quiz, overrideType) => {
    if (!user || user.isAnonymous || isReadOnly || sub.isVirtual) return;

    let newOverrides = Array.isArray(sub.overrides) ? [...sub.overrides] : [];
    let newForcedIncorrect = Array.isArray(sub.forcedIncorrect) ? [...sub.forcedIncorrect] : [];
    
    // 1. Clear any existing overrides for this specific question
    newOverrides = newOverrides.filter(idx => idx !== qIdx);
    newForcedIncorrect = newForcedIncorrect.filter(idx => idx !== qIdx);

    // 2. Apply the new override state
    if (overrideType === "correct") {
        newOverrides.push(qIdx);
    } else if (overrideType === "incorrect") {
        newForcedIncorrect.push(qIdx);
    }

    // 3. Recalculate Natural Correct vs Total Correct to find Net Adjustment
    let naturalCorrectCount = 0;
    let totalCorrectCount = 0;

    quiz.questions.forEach((q, idx) => {
        const studentAns = String(sub.answers?.[idx] || "").trim().toLowerCase();
        const correctAns = String(q.answer || "").trim().toLowerCase();
        const isNaturallyCorrect = studentAns === correctAns;
        
        if (isNaturallyCorrect) naturalCorrectCount++;

        const isForcedCorrect = newOverrides.includes(idx);
        const isForcedIncorrect = newForcedIncorrect.includes(idx);
        
        if (isForcedCorrect || (isNaturallyCorrect && !isForcedIncorrect)) {
            totalCorrectCount++;
        }
    });

    const total = quiz.questions.length || 1;
    const newScore = Math.round((totalCorrectCount / total) * 100);
    const newNetAdjustment = totalCorrectCount - naturalCorrectCount;

    try {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'submissions', sub.id), {
            overrides: newOverrides,
            forcedIncorrect: newForcedIncorrect,
            correctCount: totalCorrectCount,
            score: newScore,
            netAdjustment: newNetAdjustment !== 0 ? newNetAdjustment : null,
            manualAdjustment: null 
        });
    } catch (err) {
        console.error("Failed to override question:", err);
    }
  };

  const handleLoadSandboxData = async () => {
    if (!user || user.isAnonymous || isReadOnly) return;
    setIsInjectingMockData(true);
    try {
      const testCourseName = "TESTCOURSE - Alpha Flight";
      const roster = ["Alpha", "Bravo", "Charlie", "Delta", "Echo"];
      await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'courses'), { name: testCourseName, roster: roster, lifetimeQuizCount: 10, createdAt: new Date().toISOString() });

      const dummyQuestions = [
        { question: "What is V1?", options: ["Takeoff decision speed", "Rotation speed", "Stall speed", "Maneuvering speed"], answer: "Takeoff decision speed", explanation: "V1 is the max speed at which a rejected takeoff can be initiated.", topic: "Aircraft Limitations" },
        { question: "What causes an aircraft to stall?", options: ["Exceeding critical angle of attack", "Low airspeed", "High weight", "Engine failure"], answer: "Exceeding critical angle of attack", explanation: "A stall always occurs when the critical AOA is exceeded.", topic: "Aerodynamics" },
        { question: "What is the standard emergency squawk code?", options: ["7700", "7600", "7500", "1200"], answer: "7700", explanation: "7700 is the standard ICAO code for general emergencies.", topic: "Emergency Procedures" },
        { question: "Which instrument relies on the pitot tube?", options: ["Airspeed Indicator", "Altimeter", "Vertical Speed Indicator", "Turn Coordinator"], answer: "Airspeed Indicator", explanation: "Only the ASI uses ram air from the pitot tube.", topic: "Flight Instruments" },
        { question: "What does ATIS stand for?", options: ["Automatic Terminal Information Service", "Air Traffic Information System", "Aviation Terminal Instruction Service", "Automated Traffic Instruction Service"], answer: "Automatic Terminal Information Service", explanation: "ATIS provides continuous broadcast of recorded aeronautical information.", topic: "Radio Telephony" }
      ];

      const publicDummy = dummyQuestions.map(q => { const {answer, explanation, ...rest} = q; return rest; });
      const secretDummy = dummyQuestions.map(q => ({ answer: q.answer, explanation: q.explanation }));

      const quizPromises = [];
      for (let i = 1; i <= 10; i++) {
        const pastDate = new Date(Date.now() - (11 - i) * 86400000 * 2); 
        const isExpired = [3, 6, 9].includes(i);
        const expirationDate = isExpired ? new Date(pastDate.getTime() + 86400000) : null;

        const quizPromise = addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'quizzes'), {
          title: `Quiz-${testCourseName}-${i}`, sourceFile: "Auto-Generated Mock Data", course: testCourseName, topics: ["Aircraft Limitations", "Aerodynamics", "Emergency Procedures", "Flight Instruments", "Radio Telephony"],
          difficulty: "Medium", briefing: `PRE-FLIGHT BRIEFING\n\nMock assessment #${i}.`, questions: publicDummy, roster: roster, teacherId: user.uid, createdAt: pastDate.toISOString(), startsAt: null, expiresAt: expirationDate ? expirationDate.toISOString() : null
        }).then(async (quizDocRef) => {
          await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'quiz_keys', quizDocRef.id), { keys: secretDummy });

          const subPromises = roster.map(studentName => {
            if (isExpired && (studentName === "Delta" || studentName === "Echo")) return Promise.resolve(); 
            const correctCount = Math.floor(Math.random() * 4) + 2; 
            let mockAnswers = {};
            dummyQuestions.forEach((q, idx) => { mockAnswers[idx] = (idx < correctCount) ? q.answer : q.options.find(o => o !== q.answer); });
            const submitTimeMs = pastDate.getTime() + Math.floor(Math.random() * 86400000);
            return addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'submissions'), {
              quizId: quizDocRef.id, studentName: studentName, score: (correctCount / 5) * 100, durationMs: Math.floor(Math.random() * 480000) + 120000, correctCount: correctCount,
              totalQuestions: 5, answers: mockAnswers, submittedAt: new Date(submitTimeMs).toISOString(), overrides: []
            });
          });
          await Promise.all(subPromises);
        });
        quizPromises.push(quizPromise);
      }

      const upcomingDate = new Date(Date.now() + 30 * 1000); 
      const upcomingExpiration = new Date(Date.now() + 2 * 60 * 1000); 
      
      const upcomingQuizPromise = addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'quizzes'), {
        title: `Quiz-${testCourseName}-11 (Upcoming)`, 
        sourceFile: "Auto-Generated Mock Data", 
        course: testCourseName, 
        topics: ["Aircraft Limitations", "Aerodynamics", "Emergency Procedures", "Flight Instruments", "Radio Telephony"],
        difficulty: "Medium", 
        briefing: `PRE-FLIGHT BRIEFING\n\nThis is a live test of the waiting room. Good luck, aviator!`, 
        questions: publicDummy, 
        roster: roster, 
        teacherId: user.uid, 
        createdAt: new Date().toISOString(), 
        startsAt: upcomingDate.toISOString(), 
        expiresAt: upcomingExpiration.toISOString(),
        timeLimit: 15
      }).then(async (quizDocRef) => {
          await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'quiz_keys', quizDocRef.id), { keys: secretDummy });
      });

      quizPromises.push(upcomingQuizPromise);

      await Promise.all(quizPromises);
      setCourseFilter(testCourseName);
    } catch (error) { console.error("Error loading mock data:", error); } finally { setIsInjectingMockData(false); }
  };

  const handlePrint = () => {
    const isSandbox = window.location.hostname === '' || window.location.hostname.includes('googleusercontent') || window.location.protocol === 'about:';
    if (isSandbox) {
      setPrintError("Printing is restricted inside this development sandbox. The print button will function normally once you export and host the application publicly.");
      setTimeout(() => setPrintError(""), 6000); 
    } else {
      window.print();
    }
  };

  // --- Views ---
  const HomeView = () => {
    return (
      <div className="max-w-md mx-auto mt-12 space-y-8 animate-in zoom-in">
        <div className="text-center space-y-2">
          <BrandLogo customLogo={customLogoUrl} className="w-28 h-28 text-sky-700 mx-auto mb-4" />
          <h1 className="text-4xl font-black text-slate-900 tracking-widest uppercase">RotorShed</h1>
          <p className="text-slate-500 font-bold text-xs tracking-widest uppercase">Professional Aviation Toolset</p>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 space-y-6">
          <div>
            <h2 className="text-xs font-bold text-slate-800 mb-2 uppercase tracking-wider">Are you a student?</h2>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter Quiz Code"
                value={joinCode}
                onChange={(e) => { setJoinCode(e.target.value); setJoinError(""); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-600 font-mono text-sm text-slate-800 placeholder-slate-400"
              />
              <button
                onClick={async () => {
                  setJoinError("");
                  let code = joinCode.trim();
                  if (code.includes('?quizId=')) code = code.split('?quizId=')[1].split('&')[0];
                  else if (code.includes('Quiz Code: ')) code = code.split('Quiz Code: ')[1].trim();

                  if (!code) return;
                  
                  try {
                    const quizRef = doc(db, 'artifacts', appId, 'public', 'data', 'quizzes', code);
                    const quizSnap = await getDoc(quizRef);
                    
                    if (quizSnap.exists()) {
                      const targetQuiz = quizSnap.data();
                      if (targetQuiz.expiresAt && new Date() > new Date(targetQuiz.expiresAt)) {
                        setJoinError("This quiz has expired and is no longer accessible.");
                        return;
                      }
                      setActiveQuizId(code);
                      setView('student_quiz');
                    } else {
                      setJoinError("Quiz not found. Please check the code.");
                    }
                  } catch (err) {
                    console.error("Fetch error:", err);
                    setJoinError("Network error checking code. Please try again.");
                  }
                }}
                disabled={!joinCode.trim()}
                className="bg-sky-700 text-white px-5 py-2 rounded-lg font-bold tracking-wide uppercase text-sm hover:bg-sky-800 transition-colors disabled:opacity-50"
              >
                Join
              </button>
            </div>
            {joinError && <p className="text-red-500 text-sm mt-2 font-medium">{joinError}</p>}
          </div>

          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
            <div className="relative flex justify-center"><span className="bg-white px-4 text-xs font-bold uppercase tracking-widest text-slate-400">OR</span></div>
          </div>

          <div>
            <h2 className="text-xs font-bold text-slate-800 mb-2 uppercase tracking-wider">Are you an instructor?</h2>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter Access Key"
                value={auditKeyInput}
                onChange={(e) => { setAuditKeyInput(e.target.value); setLoginError(""); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-slate-800 font-mono text-sm text-slate-800 placeholder-slate-400"
              />
              <button
                onClick={handleAuditorLogin}
                disabled={!auditKeyInput.trim()}
                className="bg-slate-800 text-white px-4 py-2 rounded-lg font-bold tracking-wide uppercase text-sm hover:bg-slate-900 transition-colors disabled:opacity-50"
              >
                View Analytics
              </button>
            </div>
            {loginError && auditKeyInput && <p className="text-red-500 text-sm mt-2 font-medium">{loginError}</p>}
          </div>

          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
            <div className="relative flex justify-center"><span className="bg-white px-4 text-xs font-bold uppercase tracking-widest text-slate-400">OR</span></div>
          </div>

          <div>
            <h2 className="text-xs font-bold text-slate-800 mb-2 uppercase tracking-wider">Administrator Portal</h2>
            {isInstructor ? (
              <button
                onClick={() => { setView("dashboard"); setCourseFilter("All"); }}
                className="w-full bg-sky-50 text-sky-800 border border-sky-200 px-4 py-3 rounded-lg font-bold hover:bg-sky-100 transition-colors flex items-center justify-center gap-2 uppercase tracking-wide text-sm"
              >
                <BarChart2 className="w-5 h-5" /> Go to Dashboard
              </button>
            ) : !showAdminPanel ? (
              <button
                onClick={() => setShowAdminPanel(true)}
                className="w-full bg-slate-50 text-slate-700 border border-slate-200 px-4 py-3 rounded-lg font-bold hover:bg-slate-100 transition-colors uppercase tracking-wide text-sm"
              >
                Admin Login
              </button>
            ) : (
              <div className="space-y-3 animate-in fade-in slide-in-from-top-2 text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Sign in to manage your assessments</p>
                
                <button
                  onClick={handleGoogleLogin}
                  disabled={isLoggingIn}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-slate-300 text-slate-800 px-4 py-3 rounded-lg font-bold hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </button>

                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                  <div className="relative flex justify-center"><span className="bg-white px-4 text-[10px] font-black tracking-widest text-slate-400 uppercase">OR MAGIC LINK</span></div>
                </div>

                {emailSent ? (
                  <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg flex flex-col items-center">
                    <CheckCircle className="w-6 h-6 mb-2 text-green-600" />
                    <p className="font-bold text-sm">Link Sent!</p>
                    <p className="text-xs mt-1">Check your inbox and click the link to sign in securely.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendEmailLink()}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-600 text-sm text-slate-800 placeholder-slate-400"
                    />
                    <button
                      onClick={handleSendEmailLink}
                      disabled={isLoggingIn || !loginEmail}
                      className="w-full bg-slate-800 text-white px-4 py-2 rounded-lg font-bold hover:bg-slate-900 transition-colors disabled:bg-slate-400 flex justify-center items-center gap-2 tracking-wide uppercase text-sm"
                    >
                      {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4" />}
                      Send Magic Link
                    </button>
                  </div>
                )}

                {isSandboxEnv && (
                  <button
                    onClick={() => { setShowAdminPanel(false); setView("dashboard"); setCourseFilter("All"); }}
                    className="w-full bg-yellow-50 text-yellow-800 border border-yellow-300 px-4 py-2 rounded-lg font-bold hover:bg-yellow-100 transition-colors flex justify-center items-center gap-2 mt-4 uppercase tracking-wide text-[10px]"
                  >
                    Bypass Login (Canvas Dev Mode)
                  </button>
                )}

                <button
                  onClick={() => setShowAdminPanel(false)}
                  className="w-full mt-2 bg-transparent text-slate-500 py-2 rounded-lg font-bold hover:text-slate-800 uppercase tracking-widest text-xs"
                >
                  Cancel
                </button>
                {loginError && <p className="text-red-500 text-xs text-center font-medium mt-2">{loginError}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const SetupView = () => {
    const handleStartsAtChange = (e) => {
      const newStart = e.target.value;
      setStartsAt(newStart);
      if (newStart) {
        const startDate = new Date(newStart);
        startDate.setMinutes(startDate.getMinutes() + 30);
        const offset = startDate.getTimezoneOffset() * 60000;
        const localISOTime = new Date(startDate.getTime() - offset).toISOString().slice(0, 16);
        setExpiresAt(localISOTime);
      } else {
        setExpiresAt("");
      }
    };

    return (
      <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in zoom-in duration-300">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase">Create a New Assessment</h1>
          <p className="text-slate-500 font-medium">Upload an aviation manual (POH, AIM, etc.) or select topics to generate a quiz.</p>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 border-dashed border-2 hover:border-sky-500 transition-colors">
          <input type="file" accept="application/pdf" onChange={handleFileUpload} className="hidden" ref={fileInputRef}/>
          {status === "extracting" ? (
            <div className="flex flex-col items-center text-sky-700">
              <Loader2 className="w-10 h-10 animate-spin mb-3" />
              <p className="font-bold text-lg animate-pulse tracking-wide uppercase">Extracting Text...</p>
            </div>
          ) : file ? (
            <div className="flex flex-col items-center text-green-700">
              <CheckCircle className="w-10 h-10 mb-3 text-green-500" />
              <p className="font-bold text-lg">{file.name}</p>
              <button onClick={() => { setFile(null); setPdfText(""); }} className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 underline">Upload a different file</button>
            </div>
          ) : (
            <div className="flex flex-col items-center cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              <div className="w-16 h-16 bg-slate-50 rounded-full shadow-sm flex items-center justify-center mb-4 border border-slate-100">
                <UploadCloud className="w-8 h-8 text-sky-600" />
              </div>
              <p className="font-bold text-slate-800 text-lg tracking-wide uppercase">Click to upload Aviation PDF</p>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mt-2">(Optional if Topics are selected below)</p>
            </div>
          )}
        </div>

        <div className="space-y-6 transition-opacity duration-500">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-4">
              <Settings className="w-5 h-5 text-slate-500" />
              <h2 className="text-lg font-black uppercase tracking-tight text-slate-900">Assessment Settings</h2>
            </div>
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex justify-between"><label className="font-bold text-slate-700 text-xs uppercase tracking-widest">Difficulty: {difficultyLabels[difficulty]}</label></div>
                <input type="range" min="1" max="5" value={difficulty} onChange={(e) => setDifficulty(parseInt(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-sky-700" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="font-bold text-slate-700 flex justify-between text-xs uppercase tracking-widest">Questions <span className="text-sky-700">{numQuestions}</span></label>
                  <input type="range" min="1" max="20" value={numQuestions} onChange={(e) => setNumQuestions(parseInt(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-sky-700" />
                </div>
                <div className="space-y-2">
                  <label className="font-bold text-slate-700 flex justify-between text-xs uppercase tracking-widest">Options <span className="text-sky-700">{numOptions}</span></label>
                  <input type="range" min="2" max="6" value={numOptions} onChange={(e) => setNumOptions(parseInt(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-sky-700" />
                </div>
              </div>

              {/* --- Topic Selection Section --- */}
              <div className="space-y-3 pt-6 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <label className="font-bold text-slate-700 flex items-center gap-2 text-xs uppercase tracking-widest">
                    <CheckSquare className="w-4 h-4 text-sky-700"/> Select Aviation Topics
                    {!pdfText && selectedTopics.length === 0 && <span className="text-[10px] text-red-500 font-black tracking-widest ml-2">(Required if no PDF)</span>}
                  </label>
                  {!isReadOnly && (
                    <button 
                      onClick={() => setIsManagingTopics(!isManagingTopics)}
                      className="text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-slate-900 flex items-center gap-1 bg-slate-100 px-2 py-1 rounded"
                    >
                      <Edit3 className="w-3 h-3" /> Edit Topics
                    </button>
                  )}
                </div>

                {isManagingTopics && (
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4 animate-in slide-in-from-top-2">
                    <div className="flex gap-2 mb-4">
                      <input 
                        type="text" 
                        placeholder="New Topic Name..." 
                        value={newTopicName} 
                        onChange={(e) => setNewTopicName(e.target.value)} 
                        onKeyDown={(e) => e.key === 'Enter' && handleAddTopic()}
                        className="flex-1 border border-slate-300 px-3 py-1.5 rounded-md outline-none focus:ring-2 focus:ring-sky-600 text-sm text-slate-800" 
                      />
                      <button 
                        onClick={handleAddTopic} 
                        disabled={!newTopicName.trim()}
                        className="bg-slate-800 text-white px-4 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider hover:bg-slate-900 disabled:opacity-50 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    {allTopics.length > 0 ? (
                      <div className="space-y-1 max-h-40 overflow-y-auto pr-2">
                        {allTopics.map(t => (
                          <div key={t.id} className="flex justify-between items-center bg-white px-3 py-2 rounded border border-slate-200 text-sm group">
                            <span className="text-slate-800 font-semibold">{t.name}</span>
                            <button onClick={() => handleRemoveTopic(t.id)} className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-3.5 h-3.5"/></button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-widest text-center mb-2">No topics saved yet.</p>
                    )}
                  </div>
                )}

                {allTopics.length === 0 ? (
                   !isReadOnly ? (
                       <button onClick={loadStandardTopics} className="w-full py-3 border-2 border-dashed border-sky-300 rounded-lg text-sky-700 font-bold uppercase tracking-wider hover:bg-sky-50 transition-colors text-xs">
                         + Load Standard Aviation Topics
                       </button>
                   ) : (
                       <p className="text-xs text-slate-500 font-bold uppercase tracking-widest text-center py-4">No topics available. Contact Admin.</p>
                   )
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {allTopics.map(t => (
                      <label key={t.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedTopics.includes(t.name) ? 'bg-sky-50 border-sky-400 shadow-sm' : 'bg-white hover:bg-slate-50 border-slate-200'}`}>
                        <input 
                          type="checkbox" 
                          className="mt-0.5 w-4 h-4 text-sky-700 rounded border-slate-300 focus:ring-sky-600" 
                          checked={selectedTopics.includes(t.name)} 
                          onChange={() => toggleTopicSelection(t.name)} 
                        />
                        <span className={`text-sm font-bold leading-snug tracking-wide ${selectedTopics.includes(t.name) ? 'text-sky-900' : 'text-slate-700'}`}>{t.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* --- Additional AI Instructions --- */}
              <div className="space-y-2 pt-6 border-t border-slate-100">
                <label className="font-bold text-slate-700 block text-xs uppercase tracking-widest">Additional AI Instructions (Optional)</label>
                <textarea placeholder="e.g., 'Include Arabic translations for key terms', 'Focus heavily on VFR operations', 'Address the student as Captain'." value={topicFocus} onChange={(e) => setTopicFocus(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg outline-none text-sm text-slate-800 placeholder-slate-400 resize-none transition-colors focus:border-sky-50 focus:ring-2 focus:ring-sky-600" rows="2" />
              </div>

              {/* --- Course & Student Selection --- */}
              <div className="space-y-2 pt-6 border-t border-slate-100">
                <label className="font-bold text-slate-700 block text-xs uppercase tracking-widest">Course / Cohort (Optional)</label>
                <select 
                  value={courseName} 
                  onChange={(e) => {
                    const val = e.target.value;
                    setCourseName(val);
                    const foundCourse = allCourses.find(c => c.name === val);
                    if (foundCourse && foundCourse.roster && foundCourse.roster.length > 0) {
                      setSelectedStudents(foundCourse.roster); // Default to all students checked
                    } else {
                      setSelectedStudents([]);
                    }
                  }} 
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-600 outline-none text-sm text-slate-800 font-bold tracking-wide bg-white"
                >
                  <option value="">Uncategorized</option>
                  {allCourses.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              {courseName && allCourses.find(c => c.name === courseName)?.roster?.length > 0 && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <label className="font-bold text-slate-700 block text-xs uppercase tracking-widest">Select Assigned Aviators</label>
                    <button 
                      onClick={() => {
                        const fullRoster = allCourses.find(c => c.name === courseName).roster;
                        if (selectedStudents.length === fullRoster.length) setSelectedStudents([]);
                        else setSelectedStudents(fullRoster);
                      }}
                      className="text-[10px] text-sky-800 hover:text-sky-900 font-black uppercase tracking-widest bg-sky-50 px-2 py-1 rounded"
                    >
                      Select / Deselect All
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {allCourses.find(c => c.name === courseName).roster.map(st => (
                      <label key={st} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${selectedStudents.includes(st) ? 'bg-sky-50 border-sky-400' : 'bg-white hover:bg-slate-50 border-slate-200'}`}>
                        <input 
                          type="checkbox" 
                          checked={selectedStudents.includes(st)}
                          onChange={() => setSelectedStudents(prev => prev.includes(st) ? prev.filter(s => s !== st) : [...prev, st])}
                          className="w-4 h-4 text-sky-700 rounded border-slate-300 focus:ring-sky-600" 
                        />
                        <span className="text-sm font-bold text-slate-800 truncate">{st}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-6 border-t border-slate-100">
                <div className="space-y-2">
                  <label className="font-bold text-slate-700 block text-xs uppercase tracking-widest">Time Limit (Mins)</label>
                  <input type="number" min="1" placeholder="No Limit" value={timeLimit} onChange={(e) => setTimeLimit(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-800 font-bold focus:ring-2 focus:ring-sky-600 outline-none text-sm" />
                </div>
                <div className="space-y-2">
                  <label className="font-bold text-slate-700 block text-xs uppercase tracking-widest">Scheduled Start</label>
                  <input type="datetime-local" value={startsAt} onChange={handleStartsAtChange} className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-800 font-bold focus:ring-2 focus:ring-sky-600 outline-none text-sm" />
                </div>
                <div className="space-y-2">
                  <label className="font-bold text-slate-700 block text-xs uppercase tracking-widest">Expiration Date</label>
                  <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-800 font-bold focus:ring-2 focus:ring-sky-600 outline-none text-sm" />
                </div>
              </div>
              <p className="text-xs text-slate-500 font-medium mt-1">Students joining before the start time see a countdown. They are blocked entirely after the exact expiration time.</p>
            </div>
          </div>

          {status === "error" && (
            <div className="p-4 bg-red-50 text-red-800 font-bold border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-600" />
              <p className="text-sm">{statusMessage}</p>
            </div>
          )}

          {isSandboxEnv && (
            <div className="flex flex-col gap-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-wider text-yellow-900">Canvas Dev Mode: Direct API Bypass</p>
                  <p className="text-xs font-bold text-yellow-700 mt-1">Use your paid Gemini API key to bypass the broken server & UAE region blocks.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={manualAiMode} onChange={() => setManualAiMode(!manualAiMode)} />
                  <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>
              {manualAiMode && (
                <div className="pt-3 border-t border-yellow-200">
                  <label className="text-xs font-black uppercase tracking-widest text-yellow-800 block mb-1">Paste Paid API Key</label>
                  <input
                    type="password"
                    value={canvasApiKey}
                    onChange={(e) => setCanvasApiKey(e.target.value)}
                    placeholder="AIzaSy..."
                    className="w-full p-2.5 text-sm bg-white border border-yellow-400 rounded-lg outline-none focus:ring-2 focus:ring-yellow-600 font-mono text-slate-800"
                  />
                  <p className="text-[10px] text-yellow-700 mt-1 uppercase font-bold tracking-widest">Not saved to code. Cleared on refresh.</p>
                </div>
              )}
            </div>
          )}

          <button 
            onClick={generateQuiz} 
            disabled={(status === "generating" || (!pdfText && selectedTopics.length === 0 && !topicFocus.trim()))} 
            className="w-full py-4 bg-slate-800 hover:bg-slate-900 disabled:bg-slate-300 text-white rounded-xl font-black uppercase tracking-widest text-sm shadow-md transition-all flex justify-center items-center gap-2"
          >
            {status === "generating" ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Generating AI Quiz...</>
            ) : manualAiMode ? (
              <><CheckCircle className="w-5 h-5" /> Generate via Direct API Key</>
            ) : (
              <><CheckCircle className="w-5 h-5" /> Generate Quiz for Review</>
            )}
          </button>
        </div>

        <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-inner mt-8">
          <div className="flex items-center gap-2 mb-4">
            <RefreshCw className="w-5 h-5 text-slate-500" />
            <h2 className="text-lg font-black uppercase tracking-tight text-slate-900">Or Import Existing Quiz Data</h2>
          </div>
          <p className="text-sm font-medium text-slate-600 mb-4">Paste the raw data copied from another RotorShed instance to bypass AI generation.</p>
          <textarea placeholder='Paste exported JSON data here...' value={importText} onChange={(e) => { setImportText(e.target.value); setStatus("idle"); setStatusMessage(""); }} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-400 outline-none text-sm font-mono resize-none mb-3 text-slate-800 placeholder-slate-400" rows="3" />
          <button onClick={handleImportQuiz} disabled={!importText.trim()} className="w-full py-3 bg-slate-300 hover:bg-slate-400 text-slate-900 disabled:opacity-50 disabled:hover:bg-slate-300 rounded-lg font-black uppercase tracking-widest text-xs transition-colors">
            Import Data & Review
          </button>
        </div>
      </div>
    );
  };

  const DashboardView = () => {
    const appUrl = getAppUrl();
    const isPlaceholder = appUrl === "https://your-future-website.com";
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(appUrl)}`;
    
    const virtualSubmissions = [];
    const now = new Date();
    myQuizzes.forEach(quiz => {
      if (quiz.expiresAt && now > new Date(quiz.expiresAt) && quiz.roster) {
        quiz.roster.forEach(studentName => {
          const hasSubmitted = allSubmissions.some(s => s.quizId === quiz.id && s.studentName.toLowerCase() === studentName.toLowerCase());
          if (!hasSubmitted) {
            virtualSubmissions.push({
              id: `virtual-${quiz.id}-${studentName}`,
              quizId: quiz.id, studentName: studentName, score: 0, durationMs: null, correctCount: 0, totalQuestions: quiz.questions?.length || 0,
              answers: {}, submittedAt: quiz.expiresAt, isVirtual: true 
            });
          }
        });
      }
    });
    const augmentedSubmissions = [...allSubmissions, ...virtualSubmissions];

    const uniqueCourses = ["All", "Uncategorized", ...new Set([
      ...allCourses.map(c => c.name), 
      ...myQuizzes.map(q => q.course || "Uncategorized").filter(c => c !== "Uncategorized")
    ])];
    
    const displayedQuizzes = (courseFilter === "All" ? myQuizzes : myQuizzes.filter(q => (q.course || "Uncategorized") === courseFilter))
      .sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeB - timeA;
      });

    const displayedSubmissions = courseFilter === "All" ? [] : augmentedSubmissions.filter(s => displayedQuizzes.some(q => q.id === s.quizId));
    const analyticsQuizzes = analyticsRange === "last5" ? displayedQuizzes.slice(0, 5) : displayedQuizzes;
    const analyticsSubmissions = courseFilter === "All" ? [] : augmentedSubmissions.filter(s => analyticsQuizzes.some(q => q.id === s.quizId));

    const courseAvgScore = analyticsSubmissions.length > 0 
      ? Math.round(analyticsSubmissions.reduce((acc, sub) => acc + sub.score, 0) / analyticsSubmissions.length) 
      : 0;

    const topicStats = {};
    if (courseFilter !== "All") {
      analyticsSubmissions.forEach(sub => {
        const quiz = analyticsQuizzes.find(q => q.id === sub.quizId);
        if (quiz && quiz.questions && Array.isArray(quiz.questions)) {
          quiz.questions.forEach((q, idx) => {
            const rawTopic = q.topic || "Uncategorized";
            const topicName = String(rawTopic).trim().replace(/[\.\s]+$/, '');
            if (!topicStats[topicName]) topicStats[topicName] = { correct: 0, total: 0 };
            topicStats[topicName].total += 1;
            const studentAns = String(sub.answers?.[idx] || "").trim().toLowerCase();
            const correctAns = String(q.answer || "").trim().toLowerCase();
            
            const isNaturallyCorrect = studentAns === correctAns;
            const isForcedCorrect = sub.overrides && sub.overrides.includes(idx);
            const isForcedIncorrect = sub.forcedIncorrect && sub.forcedIncorrect.includes(idx);
            const isCorrect = isForcedCorrect || (isNaturallyCorrect && !isForcedIncorrect);

            if (isCorrect) topicStats[topicName].correct += 1;
          });
        }
      });
    }
    const topicPerformance = Object.entries(topicStats).map(([topic, data]) => ({ topic, avg: Math.round((data.correct / data.total) * 100), count: data.total })).sort((a, b) => b.avg - a.avg);

    const { badges: courseBadges } = calculateCourseStats(courseFilter);

    const studentsData = {};
    if (courseFilter !== "All") {
      analyticsQuizzes.forEach(q => {
        if (q.roster) {
          q.roster.forEach(name => {
            const lowerName = name.toLowerCase();
            if (!studentsData[lowerName]) studentsData[lowerName] = { name: name, submissions: [], avgScore: 0, missing: 0, topicStats: {}, totalPoints: 0 };
          });
        }
      });
      analyticsSubmissions.forEach(sub => {
         const lowerName = sub.studentName.toLowerCase();
         if (!studentsData[lowerName]) studentsData[lowerName] = { name: sub.studentName, submissions: [], avgScore: 0, missing: 0, topicStats: {}, totalPoints: 0 };
         studentsData[lowerName].submissions.push(sub);

         const quiz = analyticsQuizzes.find(q => q.id === sub.quizId);
         if (quiz && quiz.questions && Array.isArray(quiz.questions)) {
           quiz.questions.forEach((q, idx) => {
             const rawTopic = q.topic || "Uncategorized";
             const topicName = String(rawTopic).trim().replace(/[\.\s]+$/, '');
             if (!studentsData[lowerName].topicStats[topicName]) studentsData[lowerName].topicStats[topicName] = { correct: 0, total: 0 };
             studentsData[lowerName].topicStats[topicName].total += 1;
             const studentAns = String(sub.answers?.[idx] || "").trim().toLowerCase();
             const correctAns = String(q.answer || "").trim().toLowerCase();
             
             const isNaturallyCorrect = studentAns === correctAns;
             const isForcedCorrect = sub.overrides && sub.overrides.includes(idx);
             const isForcedIncorrect = sub.forcedIncorrect && sub.forcedIncorrect.includes(idx);
             const isCorrect = isForcedCorrect || (isNaturallyCorrect && !isForcedIncorrect);

             if (isCorrect) studentsData[lowerName].topicStats[topicName].correct += 1;
           });
         }
      });

      analyticsQuizzes.forEach(quiz => {
        const quizSubs = analyticsSubmissions.filter(s => s.quizId === quiz.id);
        const sortedQuizSubs = [...quizSubs].sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          const timeA = a.submittedAt ? new Date(a.submittedAt).getTime() : Infinity;
          const timeB = b.submittedAt ? new Date(b.submittedAt).getTime() : Infinity;
          if (timeA !== timeB) return timeA - timeB;
          return (a.durationMs || Infinity) - (b.durationMs || Infinity);
        });

        const quizSize = (quiz.roster && quiz.roster.length > 0) ? quiz.roster.length : sortedQuizSubs.length;

        let rank = 1;
        sortedQuizSubs.forEach(sub => {
          const lowerName = sub.studentName.toLowerCase();
          if (studentsData[lowerName]) {
             if (!sub.isVirtual) {
                const points = Math.max(0, quizSize - rank + 1);
                studentsData[lowerName].totalPoints += points;
                rank++;
             }
          }
        });
      });

      Object.values(studentsData).forEach(student => {
          if (student.submissions.length > 0) student.avgScore = Math.round(student.submissions.reduce((acc, s) => acc + s.score, 0) / student.submissions.length);
          let missingCount = 0;
          analyticsQuizzes.forEach(q => {
             const inRoster = q.roster && q.roster.some(n => n.toLowerCase() === student.name.toLowerCase());
             const hasSub = student.submissions.some(s => s.quizId === q.id);
             if (inRoster && !hasSub) missingCount++;
          });
          student.missing = missingCount;
          let totalMs = 0;
          let totalQs = 0;
          student.submissions.forEach(s => {
             if (s.durationMs) { totalMs += s.durationMs; totalQs += s.totalQuestions; }
          });
          student.avgTimePerQuestion = totalQs > 0 ? Math.round(totalMs / totalQs) : null;
          student.topicPerformance = Object.entries(student.topicStats).map(([topic, data]) => ({ topic, avg: Math.round((data.correct / data.total) * 100), count: data.total })).sort((a, b) => b.avg - a.avg);
      });
    }
    const studentsList = Object.values(studentsData).sort((a, b) => {
        if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
        if (b.avgScore !== a.avgScore) return b.avgScore - a.avgScore;
        
        const timeA = a.avgTimePerQuestion !== null ? a.avgTimePerQuestion : Infinity;
        const timeB = b.avgTimePerQuestion !== null ? b.avgTimePerQuestion : Infinity;
        return timeA - timeB;
    });

    const copyCourseRankings = () => {
      let text = `🚁 *RotorShed Overall Rankings: ${courseFilter}*\n\n`;
      
      studentsList.forEach((st, index) => {
        const badges = courseBadges[st.name.toLowerCase()] || [];
        let badgeStr = "";
        if (badges.length > 0) {
          badgeStr = " (" + badges.map(b => `${b.icon} ${b.label}`).join(", ") + ")";
        }
        text += `${index + 1}. ${st.name} - ${st.totalPoints} Pts (Avg: ${st.avgScore}%)${badgeStr}\n`;
      });

      const textArea = document.createElement("textarea");
      textArea.value = text.trim();
      document.body.appendChild(textArea);
      textArea.select();
      try { 
        document.execCommand('copy'); 
        setCopiedId(`rankings-${courseFilter}`); 
        setTimeout(() => setCopiedId(null), 2000); 
      } 
      catch (err) { console.error('Copy failed', err); }
      document.body.removeChild(textArea);
    };

    const renderAccessGraph = () => {
      if (!adminAuditKey) return null;
      
      const daysToMap = parseInt(keyGraphRange);
      const graphData = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let i = daysToMap - 1; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          graphData.push({ date: d, label: d.toLocaleDateString([], { month: 'short', day: 'numeric' }), count: 0 });
      }

      adminKeyHistory.forEach(isoString => {
          const date = new Date(isoString);
          date.setHours(0,0,0,0);
          const diffTime = today.getTime() - date.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays >= 0 && diffDays < daysToMap) {
              const index = (daysToMap - 1) - diffDays;
              if (graphData[index]) {
                  graphData[index].count++;
              }
          }
      });

      const maxLogins = Math.max(1, ...graphData.map(d => d.count));
      const totalRecent = graphData.reduce((sum, d) => sum + d.count, 0);

      return (
        <div className="mt-6 border-t border-slate-200 pt-5 animate-in fade-in slide-in-from-top-1">
           <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1"><BarChart2 className="w-3 h-3"/> Usage Frequency</h4>
                <p className="text-[10px] text-slate-400 font-bold mt-0.5">{totalRecent} logins in {daysToMap} days</p>
              </div>
              <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                 <button onClick={() => setKeyGraphRange('7')} className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-colors ${keyGraphRange === '7' ? 'bg-white shadow-sm text-sky-700' : 'text-slate-500 hover:text-slate-800'}`}>7D</button>
                 <button onClick={() => setKeyGraphRange('30')} className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-colors ${keyGraphRange === '30' ? 'bg-white shadow-sm text-sky-700' : 'text-slate-500 hover:text-slate-800'}`}>30D</button>
              </div>
           </div>
           <div className="flex items-end gap-1 h-16 w-full border-b-2 border-slate-100 pb-0.5">
             {graphData.map((day, idx) => {
                const heightPct = Math.max((day.count / maxLogins) * 100, day.count > 0 ? 15 : 5);
                const color = day.count > 0 ? 'bg-sky-500' : 'bg-slate-200';
                return (
                  <div key={idx} className={`flex-1 rounded-t-sm transition-all duration-300 ${color} hover:bg-sky-600 hover:opacity-100 opacity-80 cursor-help min-w-[2px]`} style={{ height: `${heightPct}%` }} title={`${day.label}\n${day.count} logins`}></div>
                )
             })}
           </div>
           <div className="flex justify-between text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1.5">
              <span>{graphData[0].label}</span>
              <span>Today</span>
           </div>
        </div>
      );
    };

    return (
      <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in relative">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div 
              className="relative group cursor-pointer w-16 h-16 flex items-center justify-center bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden shrink-0 transition-transform active:scale-95"
              onClick={() => !isReadOnly && document.getElementById('logoUploadInput').click()}
              title={!isReadOnly ? "Click to upload custom logo" : ""}
            >
              <BrandLogo customLogo={customLogoUrl} className="w-12 h-12 text-sky-700" />
              {!isReadOnly && (
                <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <UploadCloud className="w-5 h-5 text-white" />
                </div>
              )}
              <input type="file" id="logoUploadInput" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </div>
            <div>
              <h1 className="text-2xl font-black uppercase tracking-tight text-slate-900">Instructor Dashboard</h1>
              {uniqueCourses.length > 1 && (
                <select 
                  value={courseFilter} 
                  onChange={(e) => { setCourseFilter(e.target.value); setAnalyticsRange("all"); setExpandedStudent(null); }}
                  className="bg-white border border-slate-300 text-slate-800 py-1.5 px-3 rounded-lg text-sm font-bold tracking-wide focus:ring-2 focus:ring-sky-600 outline-none mt-1"
                >
                  {uniqueCourses.map(c => <option key={c} value={c}>{c === "All" ? "All Courses" : c}</option>)}
                </select>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {!isReadOnly && isSandboxEnv && (
              <button 
                onClick={handleLoadSandboxData}
                disabled={isInjectingMockData}
                className="bg-amber-100 text-amber-900 border border-amber-300 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-amber-200 flex items-center gap-2 transition-colors disabled:opacity-50"
                title="Only visible in Canvas environment"
              >
                {isInjectingMockData ? <Loader2 className="w-4 h-4 animate-spin"/> : <Target className="w-4 h-4"/>} Load Sandbox Data
              </button>
            )}
            {!isReadOnly && (
                <button onClick={() => setIsManagingCourses(!isManagingCourses)} className={`px-4 py-2 rounded-lg text-xs font-black tracking-widest uppercase border transition-colors ${isManagingCourses ? 'bg-slate-800 text-white border-slate-800' : 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'}`}>Manage Courses</button>
            )}
            <button onClick={() => { setView('setup'); setCourseFilter('All'); }} className="bg-sky-700 text-white px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest uppercase hover:bg-sky-800">+ Create New</button>
            <button onClick={handleLogout} className="bg-slate-200 text-slate-800 px-4 py-2 rounded-lg text-xs font-black tracking-widest uppercase hover:bg-slate-300 flex items-center gap-2"><LogOut className="w-4 h-4"/> Logout</button>
          </div>
        </div>

        {!isReadOnly && isManagingCourses && (
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 animate-in fade-in slide-in-from-top-2">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1 space-y-4">
                <h2 className="text-lg font-black text-slate-900 tracking-tight uppercase mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-sky-700" /> Manage Your Courses</h2>
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <input type="text" placeholder="Course Name (e.g. Fall 2026)" value={newCourseName} onChange={(e) => setNewCourseName(e.target.value)} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-600 text-sm font-bold text-slate-800" />
                  <input type="number" min="0" placeholder="Number of Students" value={newCourseStudentCount} onChange={(e) => setNewCourseStudentCount(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddCourse()} className="w-full sm:w-48 border border-slate-300 px-4 py-2 rounded-lg outline-none focus:ring-2 focus:ring-sky-600 text-sm font-bold text-slate-800" />
                  <button onClick={handleAddCourse} disabled={!newCourseName.trim()} className="bg-slate-800 text-white px-6 py-2 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-slate-900 disabled:opacity-50 transition-colors">Add</button>
                </div>
                
                <div className="space-y-2">
                  {allCourses.length === 0 ? (
                    <p className="text-sm font-bold uppercase tracking-widest text-slate-400 text-center py-4">No courses created yet.</p>
                  ) : (
                    allCourses.map(c => {
                      if (editingCourseId === c.id) {
                        return (
                          <div key={c.id} className="bg-white p-4 rounded-xl border-2 border-sky-300 shadow-sm space-y-4">
                            <div>
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Course Name</label>
                              <input type="text" value={editCourseName} onChange={(e) => setEditCourseName(e.target.value)} className="w-full border border-slate-300 px-3 py-2 rounded-lg outline-none focus:ring-2 focus:ring-sky-600 text-sm font-bold text-slate-800" />
                            </div>
                            <div>
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Roster ({editCourseRoster.length})</label>
                              <div className="flex flex-wrap gap-2 mb-3 max-h-32 overflow-y-auto p-2 bg-slate-50 rounded-lg border border-slate-200">
                                {editCourseRoster.length === 0 && <span className="text-xs text-slate-400 font-bold p-1 italic">No aviators assigned.</span>}
                                {editCourseRoster.map(st => (
                                  <span key={st} className="flex items-center gap-1.5 bg-white border border-slate-200 px-2 py-1 rounded-md text-xs font-bold text-slate-700 shadow-sm">
                                    {st}
                                    <button onClick={() => handleRemoveStudentFromEdit(st)} className="text-slate-300 hover:text-red-500 transition-colors ml-1 focus:outline-none"><X className="w-3 h-3" /></button>
                                  </span>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <input type="text" placeholder="Add Callsign..." value={newStudentCallsign} onChange={(e) => setNewStudentCallsign(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddStudentToEdit()} className="flex-1 border border-slate-300 px-3 py-2 rounded-lg outline-none focus:ring-2 focus:ring-sky-600 text-sm font-bold text-slate-800 placeholder-slate-400" />
                                <button onClick={handleAddStudentToEdit} disabled={!newStudentCallsign.trim()} className="bg-slate-200 text-slate-800 px-4 py-2 rounded-lg font-black text-xs uppercase tracking-widest hover:bg-slate-300 disabled:opacity-50 transition-colors">Add</button>
                              </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                              <button onClick={() => setEditingCourseId(null)} className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
                              <button onClick={() => handleSaveCourseEdit(c.id, c.name)} disabled={!editCourseName.trim() || isSavingCourse} className="px-6 py-2 bg-sky-700 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-sky-800 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-sm">
                                {isSavingCourse ? <Loader2 className="w-3 h-3 animate-spin"/> : <CheckCircle className="w-3 h-3"/>} Save Changes
                              </button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={c.id} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200 group">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-slate-800 tracking-wide uppercase">{c.name}</span>
                            <span className="bg-slate-200 text-slate-700 text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-widest">{c.roster ? c.roster.length : 0} Students</span>
                          </div>
                          <div className="flex items-center gap-1 transition-opacity">
                            {deletingCourseId === c.id ? (
                              <div className="flex items-center gap-2 bg-red-50 px-2 py-1 rounded border border-red-200">
                                <span className="text-xs font-bold text-red-700">Delete all data?</span>
                                <button onClick={() => handleRemoveCourse(c.id, c.name)} className="text-xs bg-red-600 text-white px-2 py-1 rounded font-bold hover:bg-red-700">Yes</button>
                                <button onClick={() => setDeletingCourseId(null)} className="text-xs bg-slate-200 text-slate-800 px-2 py-1 rounded font-bold hover:bg-slate-300">No</button>
                              </div>
                            ) : (
                              !isReadOnly && (
                                <>
                                  <button onClick={() => handleStartEditCourse(c)} className="text-slate-400 hover:text-sky-600 p-2 rounded-md transition-colors" title="Edit Course"><Edit2 className="w-4 h-4"/></button>
                                  <button onClick={() => setDeletingCourseId(c.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-md transition-colors" title="Delete Course"><Trash2 className="w-4 h-4"/></button>
                                </>
                              )
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              
              <div className="md:w-1/3 space-y-4 border-t md:border-t-0 md:border-l border-slate-200 pt-6 md:pt-0 md:pl-8 overflow-hidden">
                <div>
                  <h3 className="font-black text-slate-900 tracking-tight uppercase">Instructor Access Key</h3>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-1">Share this code with instructors to view analytics.</p>
                </div>
                
                {adminAuditKey ? (
                  <>
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-full max-w-full overflow-hidden">
                        <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest shrink-0">Key:</span>
                        <span className="text-sm font-mono text-sky-700 font-black select-all truncate flex-1">{adminAuditKey}</span>
                        <button onClick={copyAdminKey} className="ml-1 text-sky-500 hover:text-sky-800 transition-colors shrink-0" title="Copy Key">
                          {copiedId === 'admin-key' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                      <button onClick={generateNewAuditKey} className="flex items-center gap-1.5 text-[10px] font-black text-slate-500 hover:text-slate-800 uppercase tracking-widest transition-colors px-1">
                        <RefreshCw className="w-3 h-3"/> Generate New Key
                      </button>
                    </div>
                    {renderAccessGraph()}
                  </>
                ) : (
                  <button onClick={generateNewAuditKey} className="bg-sky-700 text-white px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-sky-800 mt-2">Generate Key</button>
                )}
              </div>
            </div>
          </div>
        )}

        {courseFilter !== "All" && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8 animate-in fade-in">
            <div className="bg-slate-50 border-b border-slate-200 p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-800 text-white rounded-lg"><BarChart2 className="w-6 h-6"/></div>
                  <div>
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <h2 className="text-lg font-black text-slate-900 tracking-tight uppercase">Course Analytics: {courseFilter}</h2>
                      <select value={analyticsRange} onChange={(e) => setAnalyticsRange(e.target.value)} className="bg-white text-slate-800 border border-slate-300 py-0.5 px-2 rounded-md text-xs font-bold focus:ring-2 focus:ring-sky-600 outline-none cursor-pointer">
                        <option value="all">All Time</option>
                        <option value="last5">Last 5 Assessments</option>
                      </select>
                      {!isReadOnly && (
                        <button 
                          onClick={copyCourseRankings} 
                          className="flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-800 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest transition-colors"
                        >
                          {copiedId === `rankings-${courseFilter}` ? <CheckCircle className="w-3 h-3 text-green-600"/> : <Copy className="w-3 h-3"/>}
                          {copiedId === `rankings-${courseFilter}` ? "Copied!" : "Copy Rankings"}
                        </button>
                      )}
                    </div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-600">{analyticsQuizzes.length} Assessments • {studentsList.length} Enrolled Aviators</p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  {analyticsQuizzes.length > 0 && (
                    <div className="hidden md:flex items-end gap-0.5 h-12 w-40 border-b-2 border-slate-300 pb-0.5" title="Class Performance Trend (Oldest to Newest)">
                      {[...analyticsQuizzes].reverse().map(cq => {
                         const subsForQuiz = analyticsSubmissions.filter(s => s.quizId === cq.id);
                         if (subsForQuiz.length === 0) return <div key={cq.id} className="flex-1 rounded-t-sm min-w-[4px] bg-slate-200" style={{ height: '10%' }} title={`${cq.title}\nNo submissions yet`} />;
                         const quizAvg = Math.round(subsForQuiz.reduce((acc, sub) => acc + sub.score, 0) / subsForQuiz.length);
                         const heightPct = Math.max(quizAvg, 10); 
                         const colorClass = quizAvg >= 80 ? 'bg-green-500' : quizAvg >= 60 ? 'bg-yellow-500' : 'bg-red-500';
                         return <div key={cq.id} className={`flex-1 rounded-t-sm ${colorClass} opacity-80 hover:opacity-100 transition-opacity min-w-[4px]`} style={{ height: `${heightPct}%` }} title={`${cq.title}\nClass Average: ${quizAvg}%`} />;
                      })}
                    </div>
                  )}
                  <div className="text-center">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Class Avg</p>
                    <p className="text-3xl font-black text-sky-700">{courseAvgScore}%</p>
                  </div>
                </div>
              </div>

              {topicPerformance.length > 0 && (
                <div className="mt-6 border-t border-slate-200 pt-5">
                  <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Performance by Topic</h4>
                  <div className="flex flex-wrap gap-3">
                    {topicPerformance.map(tp => (
                      <div key={tp.topic} className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-800">{tp.topic}</span>
                        <span className={`text-sm font-black ${tp.avg >= 80 ? 'text-green-600' : tp.avg >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{tp.avg}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {studentsList.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {studentsList.map(st => {
                  const chartQuizzes = [...analyticsQuizzes].reverse();
                  const stBadges = courseBadges[st.name.toLowerCase()] || [];
                  return (
                  <div key={st.name} className="flex flex-col">
                    <div onClick={() => setExpandedStudent(expandedStudent === st.name ? null : st.name)} className="p-4 flex items-center justify-between hover:bg-slate-50 cursor-pointer transition-colors">
                      <div className="flex items-center gap-4 flex-1">
                        <User className="w-10 h-10 p-2 bg-slate-100 text-slate-400 rounded-full shrink-0"/>
                        <div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <p className="font-black text-slate-900 text-lg uppercase tracking-wide">{st.name}</p>
                            {stBadges.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 sm:ml-2">
                                 {stBadges.map(b => (
                                    <span key={b.id} title={b.title} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] uppercase tracking-wider font-black shadow-sm ${b.colorClass}`}>
                                      <span className="text-sm">{b.icon}</span> <span>{b.label}</span>
                                    </span>
                                 ))}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-widest font-bold mt-1.5">
                            <span className={st.missing > 0 ? "text-orange-600 flex items-center gap-1" : "text-green-600 flex items-center gap-1"}>
                              {st.missing > 0 ? <><AlertTriangle className="w-3 h-3"/> {st.missing} Missing Assessments</> : <><CheckCircle className="w-3 h-3"/> All Caught Up</>}
                            </span>
                            {st.avgTimePerQuestion && <span className="text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3"/> {formatTimePerQuestion(st.avgTimePerQuestion)}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 shrink-0">
                        {chartQuizzes.length > 0 && (
                          <div className="hidden md:flex items-end gap-0.5 h-10 w-32 border-b-2 border-slate-300 pb-0.5" title="Performance Trend (Oldest to Newest)">
                            {chartQuizzes.map(cq => {
                               const sub = st.submissions.find(s => s.quizId === cq.id);
                               if (!sub) return <div key={cq.id} className={`flex-1 rounded-t-sm min-w-[4px] ${cq.roster?.some(n=>n.toLowerCase()===st.name.toLowerCase()) ? 'bg-orange-200' : 'bg-slate-100'}`} style={{ height: '10%' }} />;
                               const heightPct = Math.max(sub.score, 10); 
                               const colorClass = sub.score >= 80 ? 'bg-green-500' : sub.score >= 60 ? 'bg-yellow-500' : 'bg-red-500';
                               return <div key={cq.id} className={`flex-1 rounded-t-sm ${colorClass} opacity-80 hover:opacity-100 transition-opacity min-w-[4px]`} style={{ height: `${heightPct}%` }} title={`${cq.title}\nScore: ${sub.score}%`} />;
                            })}
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col items-end justify-center w-24">
                            <div className="font-black text-2xl text-slate-800 tracking-tight whitespace-nowrap">🏆 {st.totalPoints}</div>
                            <div className={`font-black text-[10px] uppercase tracking-widest mt-0.5 ${st.avgScore >= 80 ? 'text-green-600' : st.avgScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>Avg: {st.avgScore}%</div>
                          </div>
                          <div className="p-1 rounded-full bg-slate-100 text-slate-400">{expandedStudent === st.name ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
                        </div>
                      </div>
                    </div>
                    
                    {expandedStudent === st.name && (
                      <div className="bg-slate-50 p-4 border-t border-slate-100 animate-in slide-in-from-top-1">
                        {st.topicPerformance && st.topicPerformance.length > 0 && (
                          <div className="mb-6">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-2">Topic Proficiency</h4>
                            <div className="flex flex-wrap gap-2 px-2">
                              {st.topicPerformance.map(tp => (
                                <div key={tp.topic} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border shadow-sm ${tp.avg >= 80 ? 'bg-green-50 border-green-200' : tp.avg >= 60 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                                  <span className="text-xs font-bold uppercase tracking-wider text-slate-800">{tp.topic}</span>
                                  <span className={`text-xs font-black ${tp.avg >= 80 ? 'text-green-700' : tp.avg >= 60 ? 'text-yellow-700' : 'text-red-700'}`}>{tp.avg}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-2">Submission History</h4>
                        <div className="space-y-2">
                          {analyticsQuizzes.filter(q => st.submissions.some(s => s.quizId === q.id) || (q.roster && q.roster.some(n => n.toLowerCase() === st.name.toLowerCase()))).map(q => {
                            const sub = st.submissions.find(s => s.quizId === q.id);
                            const isDeletingThisSub = deletingSubData?.subId === (sub ? sub.id : null) || (sub?.isVirtual && deletingSubData?.quizId === q.id && deletingSubData?.studentName === st.name);
                            return (
                              <div key={q.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-white p-3 rounded-lg border border-slate-200 text-sm shadow-sm gap-2 group">
                                <span className="font-bold text-slate-800 truncate pr-4 flex-1">{q.title} <span className="font-medium text-slate-500">({q.sourceFile})</span></span>
                                {sub ? (
                                  <div className="flex items-center gap-4 shrink-0">
                                    {!sub.isVirtual && (
                                       <button onClick={() => { setAuditingSubId(sub.id); setAuditingQuizId(q.id); }} className="flex sm:flex items-center gap-1.5 px-2.5 py-1 bg-sky-50 text-sky-700 hover:bg-sky-100 rounded-md font-black text-[10px] uppercase tracking-widest transition-colors border border-sky-200 ml-2 shrink-0"><Search className="w-3 h-3" /> Audit</button>
                                    )}
                                    {isDeletingThisSub ? (
                                      <div className="flex items-center gap-2 bg-red-50 px-2 py-1 rounded border border-red-200">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-red-700">Delete?</span>
                                        <button onClick={executeDeleteSubmission} className="text-xs bg-red-600 text-white px-2 py-1 rounded font-bold hover:bg-red-700">Yes</button>
                                        <button onClick={() => setDeletingSubData(null)} className="text-xs bg-slate-200 text-slate-800 px-2 py-1 rounded font-bold hover:bg-slate-300">No</button>
                                      </div>
                                    ) : (
                                      <>
                                        {sub.isVirtual ? <div className="text-red-600 font-black text-[10px] uppercase tracking-widest bg-red-50 px-2 py-1 rounded border border-red-200">0% (Missed Deadline)</div> : (
                                          <>
                                            {sub.submittedAt && <div className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">{new Date(sub.submittedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
                                            {sub.durationMs && <div className="text-slate-400 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(sub.durationMs)}</div>}
                                            <div className="flex flex-col items-end">
                                              <div className="flex items-center gap-1">
                                                {(sub.netAdjustment || sub.manualAdjustment) ? <span className="text-[10px] text-orange-500 font-black tracking-widest" title="Instructor Overridden">({(sub.netAdjustment || sub.manualAdjustment) > 0 ? '+' : ''}{(sub.netAdjustment || sub.manualAdjustment)})</span> : null}
                                                <div className={`font-black w-16 text-right text-lg ${sub.score >= 80 ? 'text-green-600' : sub.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{sub.score}%</div>
                                              </div>
                                            </div>
                                          </>
                                        )}
                                        {!isReadOnly && <button onClick={() => setDeletingSubData({ subId: sub.id, isVirtual: sub.isVirtual, quizId: q.id, studentName: sub.studentName })} className="text-slate-300 hover:text-red-600 transition-colors p-1"><Trash2 className="w-4 h-4" /></button>}
                                      </>
                                    )}
                                  </div>
                                ) : <span className="text-orange-600 font-bold text-[10px] uppercase tracking-widest bg-orange-50 px-2 py-1 rounded border border-orange-200 w-max">Pending</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )})}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400 font-bold uppercase tracking-widest text-sm">No aviators or submissions tracked for this course yet.</div>
            )}
          </div>
        )}

        <div className="bg-sky-50 border border-sky-200 rounded-xl p-6 flex flex-col md:flex-row gap-6 items-center mb-8">
          <div className="flex-1 w-full">
            <h2 className="text-sky-900 font-black text-lg mb-2 flex items-center gap-2 uppercase tracking-wide"><Share2 className="w-5 h-5" /> How Students Join</h2>
            <p className="text-sky-800 mb-4 text-sm font-medium">Students need the link to this app and a specific <strong>Quiz Code</strong> to take a test.</p>
            <div className="flex items-center gap-2 bg-white px-4 py-3 rounded-lg border border-sky-200 mb-3">
              <LinkIcon className="w-5 h-5 text-slate-400 shrink-0" />
              <span className={`text-sm font-mono truncate flex-1 ${isPlaceholder ? 'text-slate-400' : 'text-slate-600'}`}>{appUrl}</span>
              <button onClick={copyAppUrl} className="text-sky-700 hover:text-sky-900 text-xs font-black uppercase tracking-widest whitespace-nowrap px-2">
                {copiedId === 'url' ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
            {isPlaceholder && <p className="text-xs text-sky-700 font-bold flex items-start gap-1"><AlertCircle className="w-4 h-4 shrink-0" /> Note: You are in a development sandbox. To get a real URL and working QR code, you must export and host this app on a public web server.</p>}
          </div>
          <div className={`shrink-0 bg-white p-3 rounded-xl border border-sky-200 shadow-sm flex flex-col items-center ${isPlaceholder ? 'opacity-50 grayscale' : ''}`}>
            <img src={qrCodeUrl} alt="App QR Code" className="w-28 h-28 object-contain" />
            <span className="text-[10px] text-sky-800 font-black uppercase tracking-widest mt-2">Scan to Open</span>
          </div>
        </div>

        {displayedQuizzes && displayedQuizzes.length > 0 ? (
          <div className="grid gap-6">
            {displayedQuizzes.map(quiz => {
              const submissions = augmentedSubmissions.filter(s => s.quizId === quiz.id);
              return (
                <div key={quiz.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="bg-slate-800 text-white px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">{quiz.course || "Uncategorized"}</span>
                        {quiz.topics && quiz.topics.map(t => <span key={t} className="bg-sky-50 text-sky-800 border border-sky-200 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">{t}</span>)}
                      </div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-black text-slate-900 tracking-tight uppercase">{quiz.title}</h3>
                        {!isReadOnly && deletingQuizId === quiz.id ? (
                          <div className="flex items-center gap-2 bg-red-50 px-2 py-1 rounded border border-red-200">
                            <span className="text-[10px] font-black uppercase tracking-widest text-red-700">Delete?</span>
                            <button onClick={() => handleDeleteQuiz(quiz.id)} className="text-xs bg-red-600 text-white px-2 py-1 rounded font-bold hover:bg-red-700">Yes</button>
                            <button onClick={() => setDeletingQuizId(null)} className="text-xs bg-slate-200 text-slate-800 px-2 py-1 rounded font-bold hover:bg-slate-300">No</button>
                          </div>
                        ) : (
                          !isReadOnly && <button onClick={() => setDeletingQuizId(quiz.id)} className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                      <p className="text-xs font-bold tracking-widest uppercase text-slate-400 mt-1">Source: {quiz.sourceFile || 'Uploaded PDF'} </p>
                      <div className="flex flex-wrap gap-x-4 mt-1.5">
                        {quiz.startsAt && <p className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-1 ${new Date() < new Date(quiz.startsAt) ? 'text-sky-600' : 'text-slate-400'}`}><Clock className="w-3 h-3"/> Starts: {new Date(quiz.startsAt).toLocaleString()}</p>}
                        {quiz.expiresAt && <p className={`text-[10px] font-black uppercase tracking-widest ${new Date() > new Date(quiz.expiresAt) ? 'text-red-500' : 'text-orange-500'}`}>{new Date() > new Date(quiz.expiresAt) ? 'Expired: ' : 'Expires: '}{new Date(quiz.expiresAt).toLocaleString()}</p>}
                      </div>
                      <div className="flex items-center gap-2 mt-3 mb-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-max">
                        <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Quiz Code:</span>
                        <span className="text-sm font-mono text-sky-700 font-black select-all">{quiz.id}</span>
                        <button onClick={() => copyQuizCode(quiz.id)} className="ml-2 text-sky-500 hover:text-sky-800 transition-colors">{copiedId === `code-${quiz.id}` ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}</button>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        <span className="bg-slate-100 px-2 py-1 rounded">{quiz.questions.length} Questions</span>
                        <span className="bg-slate-100 px-2 py-1 rounded">{quiz.difficulty}</span>
                        {quiz.timeLimit && <span className="bg-slate-100 px-2 py-1 rounded flex items-center gap-1"><Clock className="w-3 h-3"/> {quiz.timeLimit}m</span>}
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5"/> {submissions.length} Responses</span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 shrink-0 mt-4 md:mt-0">
                      {!isReadOnly && (
                        <>
                          <button onClick={() => copyQuizResults(quiz, submissions)} className="flex items-center justify-center gap-2 bg-slate-100 text-slate-700 border border-slate-200 px-4 py-2 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-colors">
                            {copiedId === `results-${quiz.id}` ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />} {copiedId === `results-${quiz.id}` ? "Copied!" : "Copy Results"}
                          </button>
                          <button onClick={() => { setActiveQuizId(quiz.id); setView('student_quiz'); }} className="flex items-center justify-center gap-2 bg-sky-50 text-sky-800 border border-sky-200 px-4 py-2 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-sky-100 transition-colors"><User className="w-4 h-4" /> Test as Student</button>
                        </>
                      )}
                      <button onClick={() => copyShareInstructions(quiz)} className="flex items-center justify-center gap-2 bg-slate-800 text-white border border-slate-900 px-4 py-2 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-slate-900 transition-colors">{copiedId === quiz.id ? <CheckCircle className="w-4 h-4" /> : <Share2 className="w-4 h-4" />} {copiedId === quiz.id ? "Copied!" : "Copy Invite"}</button>
                    </div>
                  </div>
                  
                  {submissions.length > 0 && (
                    <div className="bg-slate-50 p-4">
                      <div className="space-y-2">
                        {submissions.sort((a, b) => {
                            if (b.score !== a.score) return b.score - a.score;
                            const timeA = a.submittedAt ? new Date(a.submittedAt).getTime() : Infinity;
                            const timeB = b.submittedAt ? new Date(b.submittedAt).getTime() : Infinity;
                            if (timeA !== timeB) return timeA - timeB;
                            return (a.durationMs || Infinity) - (b.durationMs || Infinity);
                          }).map((sub, idx) => {
                          const isDeletingThisSub = deletingSubData?.subId === (sub ? sub.id : null) || (sub?.isVirtual && deletingSubData?.quizId === quiz.id && deletingSubData?.studentName === sub.studentName);
                          return (
                          <div key={sub.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 group">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className="text-slate-400 font-mono text-sm font-bold w-4 shrink-0">{idx + 1}.</span>
                              <span className="font-bold text-slate-800 uppercase tracking-wide truncate">{sub.studentName}</span>
                              {!sub.isVirtual && (
                                 <button onClick={() => { setAuditingSubId(sub.id); setAuditingQuizId(quiz.id); }} className="flex sm:flex items-center gap-1.5 px-2.5 py-1 bg-sky-50 text-sky-700 hover:bg-sky-100 rounded-md font-black text-[10px] uppercase tracking-widest transition-colors border border-sky-200 ml-2 shrink-0"><Search className="w-3 h-3" /> Audit</button>
                              )}
                            </div>
                            <div className="flex items-center gap-4 shrink-0">
                              {isDeletingThisSub ? (
                                <div className="flex items-center gap-2 bg-red-50 px-2 py-1 rounded border border-red-200">
                                  <span className="text-[10px] font-black uppercase tracking-widest text-red-700">Delete?</span>
                                  <button onClick={executeDeleteSubmission} className="text-xs bg-red-600 text-white px-2 py-1 rounded font-bold hover:bg-red-700">Yes</button>
                                  <button onClick={() => setDeletingSubData(null)} className="text-xs bg-slate-200 text-slate-800 px-2 py-1 rounded font-bold hover:bg-slate-300">No</button>
                                </div>
                              ) : (
                                <>
                                  {sub.isVirtual ? <div className="text-red-600 font-black text-[10px] uppercase tracking-widest bg-red-50 px-2 py-1 rounded border border-red-200">0% (Missed Deadline)</div> : (
                                    <>
                                      {sub.submittedAt && <div className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">{new Date(sub.submittedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
                                      {sub.durationMs && <div className="text-slate-400 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(sub.durationMs)}</div>}
                                      <div className="flex flex-col items-end">
                                        <div className="flex items-center gap-1">
                                          {(sub.netAdjustment || sub.manualAdjustment) ? <span className="text-[10px] text-orange-500 font-black tracking-widest" title="Instructor Overridden">({(sub.netAdjustment || sub.manualAdjustment) > 0 ? '+' : ''}{(sub.netAdjustment || sub.manualAdjustment)})</span> : null}
                                          <div className={`font-black w-16 text-right text-lg ${sub.score >= 80 ? 'text-green-600' : sub.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{sub.score}%</div>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                  {!isReadOnly && <button onClick={() => setDeletingSubData({ subId: sub.id, isVirtual: sub.isVirtual, quizId: quiz.id, studentName: sub.studentName })} className="text-slate-300 hover:text-red-600 transition-colors p-1"><Trash2 className="w-4 h-4" /></button>}
                                </>
                              )}
                            </div>
                          </div>
                        )})}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
            <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">{myQuizzes.length > 0 ? `No assessments found in "${courseFilter}"` : "No quizzes created yet"}</h3>
            <p className="text-slate-500 font-bold text-xs uppercase tracking-widest mt-1">{myQuizzes.length > 0 ? "Change your course filter to view your other assessments." : "Upload a PDF or enter a topic to generate your first assessment."}</p>
          </div>
        )}

        {auditingSubId && auditingQuizId && (
          <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in zoom-in duration-200">
            {(() => {
              const auditSub = augmentedSubmissions.find(s => s.id === auditingSubId);
              const auditQuiz = allQuizzes.find(q => q.id === auditingQuizId);
              if (!auditSub || !auditQuiz) return null;

              return (
                <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-b border-slate-200 bg-slate-50 gap-4 shrink-0">
                    <div>
                      <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                        <Search className="w-6 h-6 text-sky-700"/> Flight Log Audit
                      </h2>
                      <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mt-1">{auditSub.studentName} • {auditQuiz.title}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex flex-col items-end">
                        <div className="flex items-center gap-1">
                          {(auditSub.netAdjustment || auditSub.manualAdjustment) ? <span className="text-xs text-orange-500 font-black tracking-widest bg-orange-50 px-2 py-1 rounded-md border border-orange-200 mr-2" title="Instructor Overridden">Adjusted: {(auditSub.netAdjustment || auditSub.manualAdjustment) > 0 ? '+' : ''}{(auditSub.netAdjustment || auditSub.manualAdjustment)}</span> : null}
                          <span className={`text-4xl font-black ${auditSub.score >= 80 ? 'text-green-600' : auditSub.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{auditSub.score}%</span>
                        </div>
                      </div>
                      <button onClick={() => { setAuditingSubId(null); setAuditingQuizId(null); }} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors bg-white border border-slate-200 shadow-sm"><X className="w-6 h-6"/></button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50">
                    {auditSub.isVirtual ? (
                       <div className="text-center py-12">
                         <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4"/>
                         <h3 className="text-lg font-black uppercase tracking-widest text-slate-900">Virtual Submission</h3>
                         <p className="text-sm font-medium text-slate-500 mt-2">This student missed the deadline. No answers were recorded.</p>
                       </div>
                    ) : (
                      auditQuiz.questions?.map((q, qIdx) => {
                        const studentAns = String(auditSub.answers?.[qIdx] || "").trim().toLowerCase();
                        const correctAnsText = String(q.answer || "").trim().toLowerCase();
                        
                        const isNaturallyCorrect = studentAns === correctAnsText;
                        const isOverriddenCorrect = auditSub.overrides && auditSub.overrides.includes(qIdx);
                        const isOverriddenIncorrect = auditSub.forcedIncorrect && auditSub.forcedIncorrect.includes(qIdx);
                        const isCorrect = isOverriddenCorrect || (isNaturallyCorrect && !isOverriddenIncorrect);
                        const hasAnyOverride = isOverriddenCorrect || isOverriddenIncorrect;

                        return (
                          <div key={qIdx} className={`bg-white p-6 rounded-xl border-l-4 shadow-sm border border-slate-100 ${isCorrect ? 'border-l-green-500' : 'border-l-red-500'}`}>
                            <div className="flex justify-between items-start gap-4 mb-2">
                               <h4 className="font-bold text-slate-900">{qIdx + 1}. {q.question}</h4>
                               {!isReadOnly && (
                                 <div className="shrink-0 flex items-center gap-2">
                                     {hasAnyOverride ? (
                                         <button 
                                            onClick={() => handleOverrideQuestion(auditSub, qIdx, auditQuiz, "clear")} 
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors border shadow-sm bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
                                            title="Remove override and restore original grading"
                                         >
                                           <X className="w-3 h-3"/> Remove Override
                                         </button>
                                     ) : (
                                         isNaturallyCorrect ? (
                                             <button 
                                                onClick={() => handleOverrideQuestion(auditSub, qIdx, auditQuiz, "incorrect")} 
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors border shadow-sm bg-white text-slate-500 border-slate-200 hover:text-red-600 hover:border-red-300"
                                                title="Force this answer to be incorrect (subtracts from Score and Topic Avg)"
                                             >
                                                <X className="w-3 h-3"/> Mark Incorrect
                                             </button>
                                         ) : (
                                             <button 
                                                onClick={() => handleOverrideQuestion(auditSub, qIdx, auditQuiz, "correct")} 
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors border shadow-sm bg-white text-slate-500 border-slate-200 hover:text-green-600 hover:border-green-300"
                                                title="Force this answer to be correct (adds to Score and Topic Avg)"
                                             >
                                                <CheckCircle className="w-3 h-3"/> Mark Correct
                                             </button>
                                         )
                                     )}
                                 </div>
                               )}
                            </div>
                            <div className="mb-4 flex flex-wrap gap-2">
                              <span className="bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">{q.topic || 'General'}</span>
                              {isNaturallyCorrect && !isOverriddenIncorrect ? 
                                <span className="bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-1"><CheckCircle className="w-3 h-3"/> Correct</span> : 
                                (isOverriddenCorrect ? 
                                   <span className="bg-amber-50 text-amber-700 border border-amber-300 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-1"><CheckCircle className="w-3 h-3"/> Overridden (Correct)</span> :
                                   (isOverriddenIncorrect ?
                                       <span className="bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-1"><X className="w-3 h-3"/> Overridden (Incorrect)</span> :
                                       <span className="bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-1"><X className="w-3 h-3"/> Incorrect</span>
                                   )
                                )
                              }
                            </div>
                            <div className="space-y-2 mb-4">
                              <div className="flex items-start gap-2 text-sm"><span className="font-black uppercase tracking-widest text-[10px] w-24 shrink-0 text-slate-400 pt-0.5">Student Picked</span><span className={isCorrect ? 'text-green-700 font-bold' : 'text-red-600 font-bold'}>{auditSub.answers?.[qIdx] || "No answer"}</span></div>
                              {!isNaturallyCorrect && <div className="flex items-start gap-2 text-sm"><span className="font-black uppercase tracking-widest text-[10px] w-24 shrink-0 text-slate-400 pt-0.5">Correct Answer</span><span className="text-green-700 font-bold">{q.answer}</span></div>}
                            </div>
                            <div className="bg-sky-50 border border-sky-100 p-4 rounded-lg text-sm text-sky-900"><span className="font-black text-[10px] uppercase tracking-widest mr-2 text-sky-700">Explanation</span>{q.explanation}</div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const StudentQuizView = () => {
    if (!activeQuiz) return <Loader2 className="animate-spin mx-auto mt-20 text-sky-700 w-8 h-8"/>;

    const hasRoster = activeQuiz.roster && activeQuiz.roster.length > 0;
    const submittedNames = allSubmissions.filter(sub => sub.quizId === activeQuiz.id).map(sub => sub.studentName.toLowerCase());
    const availableCallsigns = hasRoster ? activeQuiz.roster.filter(name => !submittedNames.includes(name.toLowerCase())) : [];

    const handleSelectName = (inputName) => {
      const trimmedName = inputName?.trim() || "";
      if (!trimmedName) { setNameError(hasRoster ? "Please select your callsign." : "Please enter your name."); return; }
      if (submittedNames.includes(trimmedName.toLowerCase())) { setNameError("This callsign has already been used."); return; }
      setNameError("");
      setPendingStudentName(trimmedName);
    };

    const confirmIdentity = () => {
      setStudentName(pendingStudentName);
      setQuizStartTime(Date.now());
      autoSubmitLockRef.current = false;
      setSubmitError("");
      setPendingStudentName("");
    };

    const cancelIdentity = () => {
      setPendingStudentName("");
    };

    const { badges: aviatorBadges, leaderboard } = calculateCourseStats(activeQuiz.course);

    // --- Anti-Screenshot Security Shield ---
    if (studentName && !isWindowFocused && !studentScore && !isExitingQuiz && timeRemaining === null) {
      return (
        <div className="fixed inset-0 z-[10000] bg-slate-900/95 backdrop-blur-md flex flex-col items-center justify-center text-white p-8 select-none">
            <AlertTriangle className="w-20 h-20 text-red-500 mb-6 animate-pulse" />
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-widest text-center mb-4">Security Lock</h2>
            <p className="text-base sm:text-lg font-medium text-slate-300 text-center max-w-md">
                Window focus lost. Snipping tools, split-screen, and external applications are strictly prohibited.
            </p>
            <p className="text-[10px] font-black text-slate-500 mt-12 uppercase tracking-widest">Click anywhere to resume assessment</p>
        </div>
      );
    }

    const renderWatermark = () => (
      <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden opacity-[0.03] flex flex-wrap content-start justify-start select-none mix-blend-multiply">
        {Array.from({ length: 150 }).map((_, i) => (
          <span key={i} className="transform -rotate-45 text-2xl font-black text-slate-900 whitespace-nowrap m-8">
            {studentName}
          </span>
        ))}
      </div>
    );

    const renderBriefingModal = () => {
      if (!showWaitingRoomBriefing || !activeQuiz.briefing) return null;
      return (
        <div className="fixed inset-0 z-[5000] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in zoom-in duration-200">
           <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden text-left">
              <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-slate-50 shrink-0">
                 <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                   <BookOpen className="w-6 h-6 text-sky-700"/> Pre-Flight Briefing
                 </h3>
                 <button onClick={() => setShowWaitingRoomBriefing(false)} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors bg-white border border-slate-200 shadow-sm"><X className="w-5 h-5"/></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-white relative">
                 <div className="text-slate-800 leading-relaxed text-sm font-medium briefing-content" dangerouslySetInnerHTML={{ __html: activeQuiz.briefing }}></div>
              </div>
              <div className="p-4 border-t border-slate-200 bg-slate-50 shrink-0 flex justify-center">
                <button onClick={() => setShowWaitingRoomBriefing(false)} className="bg-slate-800 text-white font-black uppercase tracking-widest text-xs py-3 px-8 rounded-lg shadow-sm hover:bg-slate-900 transition-colors">Close Briefing</button>
              </div>
           </div>
        </div>
      );
    };

    const renderTimerBanner = () => {
      if (activeQuizTimeLeft === null) return null;
      const totalSeconds = Math.floor(activeQuizTimeLeft / 1000);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      const isWarning = totalSeconds <= 60;
      
      return (
        <div className={`sticky top-0 z-50 w-full p-3 font-black text-center uppercase tracking-widest shadow-md flex items-center justify-center gap-2 transition-colors ${isWarning ? 'bg-red-600 text-white animate-pulse' : 'bg-slate-900 text-white'}`}>
          <Clock className="w-5 h-5" />
          Time Remaining: {mins.toString().padStart(2, '0')}:{secs.toString().padStart(2, '0')}
        </div>
      );
    };

    if (timeRemaining !== null && timeRemaining > 0) {
      const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
      
      return (
        <div className="max-w-md mx-auto mt-12 bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center animate-in zoom-in">
          <BrandLogo customLogo={customLogoUrl} className="w-24 h-24 text-sky-700 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight uppercase">{activeQuiz.title}</h2>
          <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mb-8 mt-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Quiz Opens In</p>
            <div className="flex justify-center items-center gap-4 text-slate-800 font-black text-3xl">
              <div>{days}d</div>:<div>{hours.toString().padStart(2, '0')}h</div>:<div>{minutes.toString().padStart(2, '0')}m</div>:<div>{seconds.toString().padStart(2, '0')}s</div>
            </div>

            {(activeQuiz.expiresAt || activeQuiz.timeLimit) && (
              <div className="mt-6 pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-center gap-2">
                {activeQuiz.timeLimit && (
                  <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-[10px] font-black text-slate-600 uppercase tracking-widest shadow-sm w-full sm:w-auto justify-center">
                    <Clock className="w-3.5 h-3.5 text-sky-600" />
                    Time Limit: {activeQuiz.timeLimit} Mins
                  </span>
                )}
                {activeQuiz.expiresAt && (
                  <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-[10px] font-black text-slate-600 uppercase tracking-widest shadow-sm w-full sm:w-auto justify-center">
                    <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                    Closes: {new Date(activeQuiz.expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            )}
            
            {activeQuiz.briefing && (
               <button onClick={() => setShowWaitingRoomBriefing(true)} className="w-full mt-6 bg-sky-700 hover:bg-sky-800 text-white font-black uppercase tracking-widest text-sm py-4 rounded-xl shadow-md transition-all flex items-center justify-center gap-2">
                 <BookOpen className="w-5 h-5"/> Study Pre-Flight Briefing
               </button>
            )}
          </div>

          {leaderboard && leaderboard.length > 0 && (
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mb-8 text-left">
              <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                <Award className="w-4 h-4 text-sky-700"/> Current Standings
              </h3>
              <div className="space-y-2">
                {leaderboard.map((st, idx) => {
                  const badges = aviatorBadges[st.name.toLowerCase()] || [];
                  return (
                    <div key={st.name} className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm border border-slate-200">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="text-slate-400 font-mono text-sm font-bold w-4 shrink-0 pt-0.5">{idx + 1}.</span>
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-black uppercase tracking-wide text-slate-800 truncate">{st.name}</span>
                          {badges.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5 pr-2">
                              {badges.map(b => (
                                <span key={b.id} title={b.title} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-widest font-black shadow-sm ${b.colorClass}`}>
                                  <span>{b.icon}</span> <span>{b.label}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 pl-2">
                         <div className="flex flex-col items-end">
                            <span className="font-black text-slate-800 tracking-tight">🏆 {st.totalPoints}</span>
                            <span className={`text-[9px] font-bold uppercase tracking-widest mt-0.5 ${st.avgScore >= 80 ? 'text-green-600' : st.avgScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>Avg: {st.avgScore}%</span>
                         </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <button onClick={() => { setActiveQuizId(null); setJoinCode(""); setView("home"); autoSubmitLockRef.current = false; setSubmitError(""); }} className="w-full bg-slate-100 py-3 rounded-lg font-black uppercase tracking-widest text-[10px] hover:bg-slate-200 transition-colors text-slate-600">Return Home</button>
          {renderBriefingModal()}
        </div>
      );
    }

    if (pendingStudentName && !studentName) {
      return (
        <div className="max-w-md mx-auto mt-12 bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center animate-in zoom-in">
          <BrandLogo customLogo={customLogoUrl} className="w-16 h-16 text-sky-700 mx-auto mb-4" />
          <h2 className="text-xl font-black text-slate-900 mb-6 tracking-tight uppercase">Confirm Identity</h2>
          
          <div className="bg-sky-50 p-6 rounded-xl border border-sky-200 mb-8">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Are you logging in as:</p>
            <p className="text-3xl font-black text-sky-800 uppercase tracking-wide">{pendingStudentName}?</p>
          </div>

          <div className="flex flex-col gap-3">
            <button onClick={confirmIdentity} className="w-full bg-sky-700 text-white font-black uppercase tracking-widest text-sm py-4 rounded-xl shadow-sm hover:bg-sky-800 transition-colors flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" /> Affirmative (Proceed)
            </button>
            <button onClick={cancelIdentity} className="w-full bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs py-4 rounded-xl shadow-sm hover:bg-slate-200 transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" /> Negative (Go Back)
            </button>
          </div>
        </div>
      );
    }

    if (!studentName) {
      return (
        <div className="max-w-xl mx-auto mt-12 bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center animate-in zoom-in">
          <BrandLogo customLogo={customLogoUrl} className="w-24 h-24 text-sky-700 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight uppercase">{activeQuiz.title}</h2>
          <p className="text-green-600 font-bold text-sm uppercase tracking-widest mb-6 flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4"/> Assessment is Open</p>
          <div className="space-y-4 text-left">
            {hasRoster ? (
              availableCallsigns.length > 0 ? (
                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Select your Callsign to begin:</label>

                  <div className="grid grid-cols-1 gap-3 max-h-[50vh] overflow-y-auto p-1">
                    {availableCallsigns.map(c => {
                      const badges = aviatorBadges[c.toLowerCase()] || [];
                      return (
                        <div 
                          key={c}
                          onClick={() => handleSelectName(c)}
                          className="flex flex-col p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-sky-400 hover:ring-2 hover:ring-sky-100 cursor-pointer transition-all active:scale-95"
                        >
                           <span className="font-black text-slate-800 text-lg uppercase tracking-wide">{c}</span>
                           {badges.length > 0 && (
                             <div className="flex flex-wrap gap-2 mt-2">
                               {badges.map(b => (
                                 <span 
                                   key={b.id} 
                                   title={b.title}
                                   className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] uppercase tracking-widest font-black shadow-sm ${b.colorClass}`}
                                 >
                                   <span className="text-sm">{b.icon}</span> <span>{b.label}</span>
                                 </span>
                               ))}
                             </div>
                           )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 text-green-800 p-4 rounded-lg border border-green-200 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="font-bold">All Assignments Completed</p>
                  <p className="text-sm mt-1">Every callsign has successfully submitted this quiz.</p>
                </div>
              )
            ) : (
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Enter your name to begin:</label>
                <input type="text" id="studentNameInput" placeholder="e.g. Captain Smith" onChange={() => setNameError("")} onKeyDown={(e) => { if (e.key === 'Enter') handleSelectName(e.target.value); }} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 outline-none font-bold text-slate-800 transition-colors ${nameError ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-sky-600'}`} />
              </div>
            )}
            {nameError && <p className="text-red-600 text-xs font-bold uppercase tracking-widest text-center">{nameError}</p>}
            {(!hasRoster && availableCallsigns.length === 0) && (
              <button onClick={() => handleSelectName(document.getElementById('studentNameInput').value)} className="w-full bg-sky-700 text-white font-black uppercase tracking-widest text-xs py-4 rounded-lg hover:bg-sky-800 transition-colors mt-2">Check In & Continue</button>
            )}

            {activeQuiz.briefing && (
              <button onClick={() => setShowWaitingRoomBriefing(true)} className="w-full mt-2 bg-sky-50 text-sky-800 border border-sky-200 font-black uppercase tracking-widest text-xs py-4 rounded-lg shadow-sm hover:bg-sky-100 transition-colors flex items-center justify-center gap-2">
                <BookOpen className="w-4 h-4"/> Study Pre-Flight Briefing
              </button>
            )}

            <button onClick={() => { setNameError(""); setActiveQuizId(null); setJoinCode(""); setView("home"); autoSubmitLockRef.current = false; setSubmitError(""); }} className="w-full bg-slate-100 text-slate-600 font-black uppercase tracking-widest text-[10px] py-3 rounded-lg hover:bg-slate-200 transition-colors mt-3">Cancel & Return Home</button>
          </div>
          {renderBriefingModal()}
        </div>
      );
    }

    return (
      <div className="w-full relative pb-12 select-none" onCopy={e=>e.preventDefault()} onCut={e=>e.preventDefault()} onPaste={e=>e.preventDefault()} onContextMenu={e=>e.preventDefault()}>
        {renderWatermark()}
        {renderTimerBanner()}
        <div className={`max-w-2xl mx-auto space-y-8 animate-in slide-in-from-bottom-4 relative z-10 ${activeQuizTimeLeft !== null ? 'mt-8' : ''}`}>
          <div className={`bg-white p-6 rounded-xl shadow-sm border border-slate-200 sticky z-20 ${activeQuizTimeLeft !== null ? 'top-16' : 'top-4'}`}>
            <div className="flex justify-between items-center gap-4">
              <h2 className="font-black text-lg text-slate-900 uppercase tracking-tight truncate flex-1">{activeQuiz.title}</h2>
              <div className="bg-sky-50 text-sky-800 px-3 py-1 border border-sky-200 rounded-lg text-xs font-black uppercase tracking-widest shrink-0">{Object.keys(studentAnswers).length} / {activeQuiz.questions.length} Answered</div>
            </div>
          </div>

          <div className="space-y-6">
            {activeQuiz.questions.map((q, qIdx) => (
              <div key={qIdx} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">{qIdx + 1}. {q.question}</h3>
                <div className="space-y-2">
                  {q.options.map((opt, oIdx) => (
                    <label key={oIdx} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${studentAnswers[qIdx] === opt ? 'bg-sky-50 border-sky-400' : 'hover:bg-slate-50 border-slate-200'}`}>
                      <input type="radio" name={`question-${qIdx}`} className="mt-1 w-4 h-4 text-sky-700" checked={studentAnswers[qIdx] === opt} onChange={() => setStudentAnswers(prev => ({...prev, [qIdx]: opt}))} />
                      <span className="text-slate-800 font-medium leading-snug">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {submitError && (
            <div className="bg-red-50 text-red-800 p-4 rounded-xl border border-red-200 text-sm font-bold flex items-start gap-3">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-600" />
              <p>{submitError}</p>
            </div>
          )}

          <button onClick={() => handleStudentSubmit(activeQuiz)} disabled={Object.keys(studentAnswers).length < activeQuiz.questions.length || isExitingQuiz} className="w-full bg-slate-800 disabled:bg-slate-300 text-white font-black uppercase tracking-widest text-sm py-5 rounded-xl shadow-md transition-all hover:bg-slate-900 flex justify-center items-center gap-2">
            {isExitingQuiz ? <><Loader2 className="w-5 h-5 animate-spin"/> Transmitting...</> : 'Submit Answers'}
          </button>
        </div>
      </div>
    );
  };

  const StudentResultView = () => {
    if (!activeQuiz || !studentScore) return null;

    const renderWatermark = () => (
      <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden opacity-[0.03] flex flex-wrap content-start justify-start select-none mix-blend-multiply">
        {Array.from({ length: 150 }).map((_, i) => (
          <span key={i} className="transform -rotate-45 text-2xl font-black text-slate-900 whitespace-nowrap m-8">
            {studentName}
          </span>
        ))}
      </div>
    );

    return (
      <div className="w-full relative pb-12 select-none" onCopy={e=>e.preventDefault()} onCut={e=>e.preventDefault()} onPaste={e=>e.preventDefault()} onContextMenu={e=>e.preventDefault()}>
        {renderWatermark()}
        <div className="max-w-2xl mx-auto space-y-6 animate-in slide-in-from-bottom-4 relative z-10">
          <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center mt-12">
            <Award className={`w-16 h-16 mx-auto mb-4 ${studentScore.pct >= 80 ? 'text-green-500' : studentScore.pct >= 60 ? 'text-yellow-500' : 'text-red-500'}`} />
            <h2 className="text-3xl font-black uppercase tracking-tight text-slate-900 mb-2">Assessment Complete</h2>
            <p className="text-slate-500 font-bold mb-6">Great job, <span className="text-slate-800">{studentName}</span>. Your results have been submitted.</p>
            <div className="inline-flex flex-col items-center justify-center w-40 h-40 rounded-full border-8 border-slate-50 relative shadow-inner">
              <span className={`text-4xl font-black ${studentScore.pct >= 80 ? 'text-green-600' : studentScore.pct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{studentScore.pct}%</span>
              <span className="text-slate-400 font-black uppercase tracking-widest text-xs mt-1">{studentScore.correct} / {studentScore.total}</span>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-bold text-xs text-slate-500 uppercase tracking-widest px-2">Review Incorrect Answers</h3>
            {(() => {
              const incorrectQuestions = studentScore.gradedQuestions?.filter(q => !q.isCorrect) || [];

              if (incorrectQuestions.length === 0) {
                return (
                  <div className="bg-white p-8 rounded-xl shadow-sm border border-green-200 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <h4 className="text-lg font-black uppercase tracking-widest text-green-700">Flawless Execution</h4>
                    <p className="text-sm font-medium text-slate-500 mt-2">No errors to review. Excellent work!</p>
                  </div>
                );
              }

              return incorrectQuestions.map((q) => (
                <div key={q.originalIndex} className="bg-white p-6 rounded-xl border-l-4 shadow-sm border border-slate-100 border-l-red-500">
                  <h4 className="font-bold text-slate-900 mb-2">{q.originalIndex + 1}. {q.question}</h4>
                  <div className="mb-4">
                    <span className="bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">{q.topic || 'General'}</span>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2 text-sm"><span className="font-black uppercase tracking-widest text-[10px] w-24 shrink-0 text-slate-400 pt-0.5">Your Action</span><span className="text-red-600 font-bold line-through">{q.studentAnswer}</span></div>
                    <div className="flex items-start gap-2 text-sm"><span className="font-black uppercase tracking-widest text-[10px] w-24 shrink-0 text-slate-400 pt-0.5">Correct</span><span className="text-green-700 font-bold">{q.correctAnswer}</span></div>
                  </div>
                  <div className="bg-sky-50 border border-sky-100 p-4 rounded-lg text-sm text-sky-900">
                    <span className="font-black text-[10px] uppercase tracking-widest mr-2 text-sky-700">Explanation</span>{q.explanation}
                  </div>
                </div>
              ));
            })()}
          </div>

          <div className="pt-8">
            <button onClick={() => { setStudentName(""); setPendingStudentName(""); setStudentAnswers({}); setStudentScore(null); setQuizStartTime(null); setActiveQuizId(null); setView("home"); setTimeRemaining(null); setActiveQuizTimeLeft(null); setIsExitingQuiz(false); submitLockRef.current = false; autoSubmitLockRef.current = false; setSubmitError(""); setIsWindowFocused(true); }} className="w-full bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs py-5 rounded-xl shadow-sm border border-slate-200 transition-all hover:bg-slate-200 flex items-center justify-center gap-2"><Home className="w-4 h-4" /> Return to Home</button>
          </div>
        </div>
      </div>
    );
  };

  const ReviewView = () => (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in zoom-in duration-300 print:m-0 print:p-0">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-slate-200 sticky top-20 z-10 print:hidden">
        <div>
          <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Review Quiz: <span className="font-bold">{file?.name || "Topic Quiz"}</span></h2>
          <p className="text-slate-400 font-black text-[10px] uppercase tracking-widest mb-2 mt-1">{draftQuiz?.length} Questions • {difficultyLabels[difficulty]}</p>
          {selectedTopics.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedTopics.map(t => <span key={t} className="bg-sky-50 text-sky-800 border border-sky-200 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">{t}</span>)}
            </div>
          )}
        </div>
        <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
          <button onClick={() => setReviewTab("quiz")} className={`px-4 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition-colors ${reviewTab === "quiz" ? 'bg-white shadow-sm text-sky-700' : 'text-slate-500 hover:text-slate-800'}`}>Student View</button>
          <button onClick={() => setReviewTab("answers")} className={`px-4 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest transition-colors ${reviewTab === "answers" ? 'bg-white shadow-sm text-sky-700' : 'text-slate-500 hover:text-slate-800'}`}>Answer Key</button>
        </div>
        <div className="flex flex-wrap w-full md:w-auto gap-3">
          <button onClick={exportQuizData} className="flex-1 md:flex-none flex justify-center items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 border border-slate-200 hover:bg-slate-200 rounded-lg font-black text-[10px] uppercase tracking-widest transition-colors" title="Copy quiz data to import elsewhere">
            {copiedId === 'export-data' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copiedId === 'export-data' ? 'Data Copied!' : 'Export'}
          </button>
          <button onClick={handlePrint} className="flex-1 md:flex-none flex justify-center items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 border border-slate-200 hover:bg-slate-200 rounded-lg font-black text-[10px] uppercase tracking-widest transition-colors"><Printer className="w-4 h-4" /> Print</button>
          <button onClick={() => { setDraftQuiz(null); setView("setup"); }} className="flex-1 md:flex-none px-4 py-2 text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 rounded-lg font-black text-[10px] uppercase tracking-widest transition-colors">Discard</button>
          <button onClick={saveQuizToCloud} disabled={status === "saving"} className="flex-1 md:flex-none flex justify-center items-center gap-2 px-6 py-2 bg-sky-700 hover:bg-sky-800 text-white rounded-lg font-black text-[10px] uppercase tracking-widest shadow-md transition-colors disabled:bg-sky-300">
            {status === "saving" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Publish
          </button>
        </div>
      </div>

      <div className="hidden print:block mb-8 border-b-2 border-black pb-4">
        <h1 className="text-2xl font-bold uppercase tracking-widest text-black">{reviewTab === 'quiz' ? 'Knowledge Assessment' : 'Answer Key & Explanations'}</h1>
        {reviewTab === 'quiz' && <div className="flex justify-between mt-4 text-black font-semibold"><p>Name: _______________________________</p><p>Date: ________________</p></div>}
        <p className="mt-4 text-sm italic text-slate-700">Source: {file?.name || "Topic"}</p>
      </div>

      {printError && <div className="p-4 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg flex items-start gap-3 print:hidden"><AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-yellow-600" /><p className="text-sm font-bold">{printError}</p></div>}
      
      {draftBriefing ? (
        <div className="bg-sky-50 p-6 rounded-xl border border-sky-100 mb-6 print:block relative group">
          {isRegeneratingBriefing && (
            <div className="absolute inset-0 bg-sky-50/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-xl border border-sky-200 print:hidden">
              <Loader2 className="w-8 h-8 animate-spin text-sky-700 mb-2" />
              <p className="text-xs font-black uppercase tracking-widest text-sky-800">Rewriting briefing...</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
            <h3 className="text-lg font-black uppercase tracking-tight text-sky-900 flex items-center gap-2"><BookOpen className="w-5 h-5 print:hidden" /> Pre-Flight Briefing</h3>
            <button onClick={regenerateBriefing} disabled={isRegeneratingBriefing} className="flex items-center gap-2 bg-white text-sky-700 border border-sky-200 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-sky-100 transition-colors print:hidden shadow-sm" title="Rewrite the briefing to match the current list of questions below">
              <RefreshCw className={`w-3 h-3 ${isRegeneratingBriefing ? 'animate-spin' : ''}`} /> Regenerate Briefing
            </button>
          </div>
          
          <div 
            contentEditable={true} 
            suppressContentEditableWarning={true}
            onBlur={(e) => setDraftBriefing(e.target.innerHTML)}
            dangerouslySetInnerHTML={{ __html: draftBriefing }} 
            className="w-full bg-white border border-sky-200 rounded-lg p-5 text-slate-800 font-medium text-sm leading-relaxed outline-none focus:ring-2 focus:ring-sky-600 min-h-[250px] briefing-content print:hidden" 
          />
          <div className="hidden print:block text-slate-900 text-sm leading-relaxed briefing-content" dangerouslySetInnerHTML={{ __html: draftBriefing }}></div>
        </div>
      ) : (
        <div className="bg-slate-50 border-2 border-dashed border-slate-300 p-8 rounded-xl mb-6 text-center print:hidden relative overflow-hidden">
          {isRegeneratingBriefing && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-xl"><Loader2 className="w-8 h-8 animate-spin text-sky-700 mb-2" /><p className="text-xs font-black uppercase tracking-widest text-sky-800">Drafting pedagogical briefing...</p></div>
          )}
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">Pre-Flight Briefing</h3>
          <p className="text-sm font-medium text-slate-500 mb-6 mt-1 max-w-lg mx-auto">Generate a dedicated study guide based on the questions below. It will teach the underlying theory while concealing 50% of the direct answers.</p>
          <button onClick={regenerateBriefing} disabled={isRegeneratingBriefing} className="bg-sky-700 text-white font-black tracking-widest text-sm uppercase px-6 py-3 rounded-xl shadow-md transition-all hover:bg-sky-800 inline-flex items-center gap-2">
             <BookOpen className="w-5 h-5" /> Generate Briefing
          </button>
        </div>
      )}

      <div className="space-y-6 pb-12 print:pb-0">
        {draftQuiz?.map((q, idx) => (
          <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 break-inside-avoid print:p-0 print:border-none print:shadow-none print:mb-8 relative group">
            {regeneratingIndex === idx && <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-xl border border-sky-200 print:hidden"><Loader2 className="w-8 h-8 animate-spin text-sky-700 mb-2" /><p className="text-xs font-black uppercase tracking-widest text-sky-800">Drafting new question...</p></div>}
            <div className="flex justify-between items-start gap-4">
              <div className="w-full">
                <h3 className="text-lg font-bold text-slate-900 mb-2 flex gap-3 print:text-black"><span className="text-sky-700 print:text-black">{idx + 1}.</span> {q.question}</h3>
                <div className="mb-4 pl-7 print:pl-5">
                  <span className="bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest print:border-black print:text-black print:bg-transparent">{q.topic || 'General'}</span>
                </div>
              </div>
              <button onClick={() => regenerateSingleQuestion(idx)} disabled={regeneratingIndex !== null} className="shrink-0 p-2 text-slate-400 hover:text-sky-700 hover:bg-sky-50 rounded-lg transition-colors print:hidden opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-0" title="Regenerate this question"><RefreshCw className={`w-5 h-5 ${regeneratingIndex === idx ? 'animate-spin' : ''}`} /></button>
            </div>
            <div className="space-y-3 pl-7 mb-4">
              {q.options.map((opt, optIdx) => {
                const isCorrect = opt === q.answer;
                const showAnswer = reviewTab === "answers";
                return (
                  <div key={optIdx} className="flex flex-col gap-1 print:gap-1 mb-2">
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 mt-0.5 rounded-full border-2 shrink-0 print:border-black print:border ${showAnswer && isCorrect ? 'border-green-500 bg-green-100 print:bg-gray-300' : 'border-slate-300'}`}></div>
                      <label className={`leading-tight pt-0.5 font-medium print:text-black ${showAnswer && isCorrect ? 'text-green-800' : 'text-slate-700'}`}>{opt}</label>
                    </div>
                  </div>
                );
              })}
            </div>
            {reviewTab === "answers" && (
              <div className="pl-7 bg-sky-50 p-4 rounded-lg border-l-4 border-sky-500 print:bg-transparent print:border-l-2 print:border-black print:pl-4 print:p-0 mt-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-sky-800 mb-1 print:text-black">Explanation:</p>
                <p className="text-slate-700 font-medium text-sm print:text-black">{q.explanation}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (isAuthLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin w-10 h-10 text-sky-700"/></div>;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-sky-100 print:bg-white">
      {!['home', 'student_quiz', 'student_result'].includes(view) && (
        <nav className="bg-white border-b border-slate-200 sticky top-0 z-20 print:hidden">
          <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3 text-sky-800 font-black text-xl cursor-pointer tracking-widest uppercase" onClick={() => { setView("home"); setCourseFilter("All"); }}>
              <BrandLogo customLogo={customLogoUrl} className="w-10 h-10" />
              <span>RotorShed</span>
            </div>
            {(user && (!user.isAnonymous || viewingAdminId)) && (
              <div className="flex items-center gap-4">
                <button onClick={()=>{ setView('dashboard'); setCourseFilter('All'); }} className={`text-[10px] font-black tracking-widest uppercase transition-colors ${view==='dashboard'?'text-sky-700':'text-slate-400 hover:text-slate-800'}`}>Dashboard</button>
                <button onClick={()=>setView('setup')} className={`text-[10px] font-black tracking-widest uppercase transition-colors ${view==='setup'?'text-sky-700':'text-slate-400 hover:text-slate-800'}`}>Generator</button>
              </div>
            )}
          </div>
        </nav>
      )}

      <main className="p-4 md:p-8 print:p-0">
        {view === "home" && HomeView()}
        {view === "setup" && SetupView()}
        {view === "dashboard" && DashboardView()}
        {view === "review" && ReviewView()}
        {view === "student_quiz" && StudentQuizView()}
        {view === "student_result" && StudentResultView()}
      </main>

      {authErrorMsg && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-3 rounded-lg shadow-xl text-sm font-bold animate-in slide-in-from-right-4 flex items-center gap-2 max-w-sm z-50">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <p>Auth Error: {authErrorMsg}</p>
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        .briefing-content h1 { font-size: 1.5rem; font-weight: 900; margin-top: 1.5rem; margin-bottom: 0.75rem; color: #0f172a; }
        .briefing-content h2 { font-size: 1.25rem; font-weight: 900; margin-top: 1.25rem; margin-bottom: 0.5rem; color: #0f172a; }
        .briefing-content h3 { font-size: 1.125rem; font-weight: 900; margin-top: 1rem; margin-bottom: 0.5rem; color: #0f172a; }
        .briefing-content p { margin-bottom: 1rem; }
        .briefing-content ul { list-style-type: disc; margin-left: 1.5rem; margin-bottom: 1rem; margin-top: 0.5rem; }
        .briefing-content li { margin-bottom: 0.25rem; }
        .briefing-content strong { font-weight: 700; color: #0f172a; }
        
        @media print {
          body { background-color: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:text-black { color: black !important; }
          .break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
          @page { margin: 2cm; size: A4; }
        }
      `}} />
    </div>
  );
}